import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertCircle,
    BarChart3,
    Check,
    CheckCircle2,
    ChevronRight,
    Clock3,
    Coins,
    Copy,
    Download,
    Gift,
    History,
    Info,
    Landmark,
    Plus,
    Search,
    Send,
    Settings2,
    ShieldCheck,
    Sparkles,
    Square,
    ToggleLeft,
    ToggleRight,
    UserCheck,
    X,
    type LucideIcon,
} from 'lucide-react';
import ProductSelectModal from './components/ProductSelectModal';
import SendConfirmModal, { SendConfirmPayload, SendConfirmResult, SendProduct, SendRecipient } from './components/SendConfirmModal';
import HistoryDetailModal from './components/HistoryDetailModal';
import TrainerPermissionModal from './components/TrainerPermissionModal';
import { useSearchParams } from 'react-router-dom';
import {
    buildHistoryQuery,
    toApiSegmentKey,
    useGifticonServerData,
} from './useGifticonServerData';
import { useAutomationExecutionsQuery } from '../../services/useGifticonQueryService';
import {
    useCancelGiftBatchMutation,
    useConfirmGiftBatchPaymentMutation,
    useCreateAutomationRuleMutation,
    useCreateGiftChargeMutation,
    useDeleteAutomationRuleMutation,
    useDeleteHeldItemMutation,
    useResendHeldItemMutation,
    useSendGiftMutation,
    useSetRecommendedProductMutation,
    useUpdateAutomationLimitMutation,
    useUpdateAutomationRuleMutation,
    useUpdateBudgetPermissionMutation,
    useUpdateGiftSettingsMutation,
} from '../../services/useGifticonMutationService';
import { exportGiftHistoryAPI, parseGiftError } from '../../api/gifticon';
import { usePublicMemberListAll } from '@/features/publicMember/hooks';
import type { ICenterMember } from '@/features/publicMember/types';
import { getItemWithExpiration, formatAmountText } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { AutomationTiming, AutomationTriggerType, ChargePolicy, ChargeTier, GiftCatalogProduct, GiftHistoryDisplayStatus } from '../../types/gifticon';
import { GIFT_CHECKOUT_BATCH_KEY, GIFT_CHECKOUT_QUERY_KEY, requestGiftBatchCheckout } from './lib/checkoutToss';
import { buildGroupSendIdempotencyKey } from './lib/sendIdempotencyKey';
import { getHistoryStatusTone, HistoryStatusLabel } from './historyStatus';

type GiftTab = 'home' | 'send' | 'automation' | 'history';
type SendTargetMode = 'direct' | 'recommended';
/** 보류된 자동 발송 행 — useGifticonServerData.heldAutomation 매핑 결과와 동일한 형태 */
type HeldAutomationItem = { id: string; receiver: string; rule: string; product: string; amount: number; reason: string };
export type SegmentKey = 'birthday' | 'rereg' | 'absent' | 'milestone' | 'newMember' | 'excellent' | 'anniversary';
type HistoryPeriod = 'all' | 'today' | 'week' | 'month' | 'custom';
export type ChargeStatus = '입금 대기' | '입금 확인 중' | '충전 완료' | '취소';

export type Segment = {
    key: SegmentKey;
    title: string;
    description: string;
    /** 상세 기준문(ⓘ 툴팁·배너) — 구서버 응답에는 없어 빈 문자열이면 표기 생략 */
    criteria: string;
    /** 자동발송 재발동 주기 문구 — 구서버 응답에는 없어 빈 문자열이면 표기 생략 */
    recurrence: string;
    /** 스냅샷 산출 시각(ISO) — 각주 "HH:mm 기준" 표기용 */
    computedAt: string | null;
    count: number;
    sentThisMonth: number;
    budgetUsed: number;
    recommendedGift: string;
    unitPrice: number;
    /** 서버 추천 상품 브리프 — 발송 시 이름 대조 없이 productId를 그대로 쓴다 */
    recommendedProduct: { id: string; brand: string; name: string; price: number } | null;
    tone: string;
    priority: '높음' | '보통' | '낮음';
};

export type Candidate = {
    id: number;
    name: string;
    segment: SegmentKey;
    detail: string;
    coach: string;
    lastGift: string;
};

/** 담당 강사가 없는 회원을 모으는 그룹 키 — 실제 강사가 아니므로 강사 발송 대상에서 제외한다 */
const NO_COACH_GROUP = '담당 강사 미지정';

/** '직접 선택' 목록 한 줄 — 센터 전체 회원 + (겹치면) 선물 타이밍 후보 사유 */
type DirectMemberRow = {
    memberId: string;
    name: string;
    coach: string;
    statusLabel: string | null;
    hasPhone: boolean;
    candidateDetail: string | null;
};

/** 회원 상태 배지 — 회원 관리 화면과 같은 기준(PT 상태 우선, 없으면 이용권·그룹수강권) */
const memberStatusLabel = (member: ICenterMember): string | null => {
    if (member.ptStatus === 'active') return '활성';
    if (member.ptStatus === 'expired') return '만료';
    if (member.ptStatus === 'empty') return '대기';
    if (member.hasSubscription || (member.groupTicketCount ?? 0) > 0) return '활성';
    if (member.subscriptionCount > 0 || member.hasGroupTicket) return '만료';
    return null;
};

export type AutomationRule = {
    id: string;
    name: string;
    target: '회원' | '강사';
    condition: string;
    timing: string;
    product: string;
    price: number;
    estimate: number;
    enabled: boolean;
    recent: string;
    /** 에디터 원값(F-16) — 서버 enum·productId 그대로. 구 enum 규칙은 세그먼트 키가 아닐 수 있다 */
    triggerType: string;
    timingValue: string;
    productId: string | null;
    message?: string;
};

export type TrainerPerformance = {
    id: string;
    name: string;
    role: string;
    renewals: number;
    revenue: number;
    journalRate: number;
    rating: number;
    budgetEnabled: boolean;
    monthlyLimit: number;
    usedThisMonth: number;
};

export type HistoryRow = {
    id: string;
    /** 서버 발송 건의 소속 batch — 예약 취소·재발송 API에 사용 (목업 행은 없음) */
    batchId?: string;
    date: string;
    sortAt: string;
    receiver: string;
    receiverType: '회원' | '강사' | '단체';
    reason: string;
    route: '직접 발송' | '대시보드 추천' | '자동 규칙' | '단체 캠페인' | '강사 발송';
    product: string;
    status: HistoryStatusLabel;
    /** 서버 발송 내역에만 존재. 목업 행은 status 라벨로 호환 표시한다. */
    displayStatus?: GiftHistoryDisplayStatus;
    payment: '충전 잔액' | '바로 결제';
    amount: number;
    trainerName?: string;
    reregStatus?: 'YES' | 'EXPIRED' | 'ACTIVE';
    reregCount?: number;
    recipientCount?: number;
};

export type ChargeRecord = {
    id: string;
    requestedAt: string;
    depositorName: string;
    depositAmount: number;
    discountAmount: number;
    grantedAmount: number;
    status: ChargeStatus;
};

const money = (value: number) => `${value.toLocaleString()}원`;
const point = (value: number) => `${value.toLocaleString()}P`;

/** 서버 상품 → 발송 페이로드 상품. id에 서버 productId를 실어 발송 경로의 이름 대조를 없앤다 */
const toSendProduct = (product: GiftCatalogProduct): SendProduct => ({
    id: product.productId,
    brand: product.brand,
    name: product.name,
    price: product.price,
});

const MESSAGE_TEMPLATES: Record<string, string> = {
    감사: '{이름} 회원님, 늘 함께해 주셔서 감사합니다.',
    생일: '{이름} 회원님, 생일을 진심으로 축하드려요! 오늘 하루 달콤하게 보내세요.',
    재등록: '{이름} 회원님, 다시 함께하게 되어 기뻐요. 앞으로도 잘 부탁드립니다!',
    응원: '{이름} 회원님, 요즘 바쁘시죠? 이 선물로 기운 내시고 곧 뵈어요!',
    달성: '{이름} 회원님, 대단해요! 꾸준함이 만든 성과를 축하합니다.',
};

const tabs: { key: GiftTab; label: string; icon: LucideIcon }[] = [
    { key: 'home', label: '선물 홈', icon: Sparkles },
    { key: 'send', label: '보내기', icon: Send },
    { key: 'automation', label: '자동화', icon: ToggleRight },
    { key: 'history', label: '발송 내역 및 재등록 효과', icon: History },
];

/**
 * 자동화 조건 = 세그먼트 7종 (기능기획_자동발송실행_관리자웹.md §2).
 * 타이밍보드와 동일 기준·동일 순서(서버 SEGMENT_KEYS 순서 계약)이고, 발송 시점은
 * 조건별 기본값으로 자동 결정된다(관리자 별도 선택 없음).
 */
const AUTOMATION_TRIGGER_OPTIONS: {
    value: AutomationTriggerType;
    label: string;
    desc: string;
    defaultTiming: AutomationTiming;
    timingLabel: string;
}[] = [
    { value: 'birthday', label: '생일', desc: '생일 당일', defaultTiming: 'DAY_9AM', timingLabel: '당일 오전 9시' },
    { value: 'milestone', label: '재등록 임박', desc: '수강권 잔여 회차가 얼마 남지 않았을 때', defaultTiming: 'ON_TRIGGER', timingLabel: '조건 충족 시' },
    { value: 'absent', label: '미출석·이탈 위험', desc: '최근 14일 수업 미출석', defaultTiming: 'ON_TRIGGER', timingLabel: '조건 충족 시' },
    { value: 'rereg', label: '최근 재등록', desc: '재등록 결제 완료 후', defaultTiming: 'AFTER_PAYMENT_1H', timingLabel: '결제 1시간 후' },
    { value: 'newMember', label: '신규 등록', desc: '센터 가입 당일', defaultTiming: 'JOIN_DAY', timingLabel: '등록 당일' },
    { value: 'attendance', label: '출석 우수', desc: '최근 30일 6회 이상 출석', defaultTiming: 'ON_TRIGGER', timingLabel: '조건 충족 시' },
    { value: 'anniversary', label: '센터 등록 기념일', desc: '등록 기념일 당일', defaultTiming: 'DAY_9AM', timingLabel: '당일 오전 9시' },
];

const findTriggerOption = (value: string) =>
    AUTOMATION_TRIGGER_OPTIONS.find((option) => option.value === value);

/** 세그먼트 스냅샷 산출 시각 각주용 — "8월 28일 14:05" (없거나 파싱 실패면 null → 각주 생략) */
const formatComputedAt = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${date.getMonth() + 1}월 ${date.getDate()}일 ${hh}:${mm}`;
};

const Card = ({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) => (
    <div id={id} className={`rounded-[8px] border border-[#E2E8F0] bg-white shadow-[0_6px_18px_rgba(15,23,42,0.04)] ${className}`}>
        {children}
    </div>
);

const SectionTitle = ({ title, description }: { title: string; description?: string }) => (
    <div>
        <h2 className="text-[18px] font-bold leading-[26px] text-[#1F2937]">{title}</h2>
        {description && <p className="mt-[2px] text-[13px] leading-[20px] text-[#64748B]">{description}</p>}
    </div>
);

const Badge = ({ children, tone = 'blue' }: { children: React.ReactNode; tone?: 'blue' | 'green' | 'orange' | 'gray' | 'red' }) => {
    const toneClass = {
        blue: 'bg-[#ECF1FC] text-[#3D73DD] border-[#C3D4F4]',
        green: 'bg-[#E5F8F3] text-[#008C7B] border-[#B3E8DE]',
        orange: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]',
        gray: 'bg-[#F2F4F6] text-[#4A4A4A] border-[#E5E7EB]',
        red: 'bg-[#FEF2F2] text-[#DC2626] border-[#FCA5A5]',
    }[tone];

    return <span className={`inline-flex h-[24px] items-center rounded-full border px-[9px] text-[12px] font-bold ${toneClass}`}>{children}</span>;
};

const EXECUTION_RESULT_LABELS: Record<string, string> = {
    SUCCESS: '발송 완료',
    PARTIAL: '일부 보류',
    HELD: '보류',
    SKIPPED: '건너뜀',
};

/** 규칙별 실행 이력(DX-07) — 규칙 행의 '이력' 버튼으로 펼친다 */
const RuleExecutionHistory = ({ ruleId, accessToken }: { ruleId: string; accessToken: string }) => {
    const query = useAutomationExecutionsQuery({ accessToken, ruleId, page: 1, size: 10 });
    return (
        <div className="rounded-[8px] border border-[#DDE7F8] bg-[#F8FAFF] px-[14px] py-[11px]">
            {query.isLoading && <p className="text-[12px] text-[#64748B]">실행 이력을 불러오는 중입니다…</p>}
            {query.isError && <p className="text-[12px] text-[#DC2626]">실행 이력을 불러오지 못했어요.</p>}
            {query.data && query.data.items.length === 0 && (
                <p className="text-[12px] text-[#64748B]">아직 실행된 적이 없습니다.</p>
            )}
            {query.data && query.data.items.length > 0 && (
                <div className="space-y-[6px]">
                    <p className="text-[11px] font-bold text-[#94A3B8]">최근 실행 {Math.min(query.data.totalCount, 10)}건{query.data.totalCount > 10 ? ` (전체 ${query.data.totalCount}건)` : ''}</p>
                    {query.data.items.map((execution) => (
                        <div key={execution.executionId} className="flex items-center justify-between gap-[12px] text-[12px]">
                            <span className="text-[#475569]">{new Date(execution.executedAt).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                            <span className="text-[#1F2937]">대상 {execution.targetCount}명 · {execution.amount.toLocaleString()}P</span>
                            <span className={`font-bold ${execution.result === 'SUCCESS' ? 'text-[#008C7B]' : execution.result === 'HELD' ? 'text-[#B45309]' : 'text-[#475569]'}`}>
                                {EXECUTION_RESULT_LABELS[execution.result ?? ''] ?? execution.result ?? '-'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const GifticonGiftPage = () => {
    const [searchParams] = useSearchParams();
    const dashboardActionHandled = useRef(false);
    const serverData = useGifticonServerData();
    const centerId: string = getItemWithExpiration('authObject')?.centerId ?? '';
    const createChargeMutation = useCreateGiftChargeMutation(serverData.accessToken);
    const updateBudgetPermissionMutation = useUpdateBudgetPermissionMutation(serverData.accessToken);
    const sendGiftMutation = useSendGiftMutation(serverData.accessToken);
    const setRecommendedProductMutation = useSetRecommendedProductMutation(serverData.accessToken);
    const updateSettingsMutation = useUpdateGiftSettingsMutation(serverData.accessToken);
    const updateAutomationRuleMutation = useUpdateAutomationRuleMutation(serverData.accessToken);
    const deleteAutomationRuleMutation = useDeleteAutomationRuleMutation(serverData.accessToken);
    const resendHeldItemMutation = useResendHeldItemMutation(serverData.accessToken);
    const deleteHeldItemMutation = useDeleteHeldItemMutation(serverData.accessToken);
    const cancelGiftBatchMutation = useCancelGiftBatchMutation(serverData.accessToken);
    const createAutomationRuleMutation = useCreateAutomationRuleMutation(serverData.accessToken);
    const confirmGiftPaymentMutation = useConfirmGiftBatchPaymentMutation(serverData.accessToken);
    const updateAutomationLimitMutation = useUpdateAutomationLimitMutation(serverData.accessToken);
    // 강사 월 한도 입력은 타이핑마다 PUT을 피하려고 600ms 디바운스 저장
    const limitSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const autoLimitSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const trainersRef = useRef<TrainerPerformance[]>([]);
    const requestedTab = searchParams.get('tab');
    const initialTab = tabs.some((tab) => tab.key === requestedTab) ? requestedTab as GiftTab : 'home';
    const [selectedTab, setSelectedTab] = useState<GiftTab>(initialTab);
    const [homeSegments, setHomeSegments] = useState<SegmentKey[]>([]);
    const [isSegmentSettingOpen, setIsSegmentSettingOpen] = useState(false);
    // 서버 productId — 목록이 도착하기 전에는 빈 값이고, 도착하면 첫 퀵픽이 기본 선택된다
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedPayment, setSelectedPayment] = useState<'balance' | 'card'>('balance');
    const [selectedCandidateTiming, setSelectedCandidateTiming] = useState<SegmentKey | 'all'>('all');
    const [sendTargetMode, setSendTargetMode] = useState<SendTargetMode>('direct');
    const [automationRules, setAutomationRules] = useState<AutomationRule[]>([]);
    const [autoLimit, setAutoLimit] = useState(0);
    /**
     * "빈칸"은 금액 숫자만으로 표현할 수 없다 — `Number('') === 0` 이라 한도 0P 와 구분되지 않는다.
     * 비운 시점의 값을 기억해 두고 그 값이 그대로일 때만 빈칸으로 보여준다.
     * 저장 실패 rollback 처럼 서버 값이 들어오면 sentinel 이 어긋나 실제 값이 저절로 다시 보인다.
     */
    const [autoLimitBlankAt, setAutoLimitBlankAt] = useState<number | null>(null);
    const amountCaret = useAmountInputCaret({ allowDecimal: true });
    const [heldAutomation, setHeldAutomation] = useState<HeldAutomationItem[]>([]);
    const [isRuleEditorOpen, setIsRuleEditorOpen] = useState(false);
    const [trainers, setTrainers] = useState<TrainerPerformance[]>([]);
    const [isTrainerPermissionOpen, setIsTrainerPermissionOpen] = useState(false);
    const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
    const [historyPeriod, setHistoryPeriod] = useState<HistoryPeriod>('all');
    const [historyType, setHistoryType] = useState('전체');
    const [historyRoute, setHistoryRoute] = useState('전체');
    const [historyKeyword, setHistoryKeyword] = useState('');
    const [isChargeDrawerOpen, setIsChargeDrawerOpen] = useState(false);
    const [pointBalance, setPointBalance] = useState(0);
    const [unconfirmedAmount, setUnconfirmedAmount] = useState(0);
    const [chargeRecords, setChargeRecords] = useState<ChargeRecord[]>([]);
    const [segmentList, setSegmentList] = useState<Segment[]>([]);
    const [catalogTarget, setCatalogTarget] = useState<'send' | { segment: SegmentKey } | { confirmGroup: string | null } | null>(null);
    const [customProduct, setCustomProduct] = useState<GiftCatalogProduct | null>(null);
    const [sendPayload, setSendPayload] = useState<SendConfirmPayload | null>(null);
    const [historyDetail, setHistoryDetail] = useState<HistoryRow | null>(null);
    const [homeSelectedIds, setHomeSelectedIds] = useState<number[]>([]);
    // '직접 선택'은 센터 전체 회원에서 고르므로 선택 키가 memberId(문자열)다 (후보 인덱스 id 아님)
    const [directSelectedMemberIds, setDirectSelectedMemberIds] = useState<string[]>([]);
    const [selectedCoachNames, setSelectedCoachNames] = useState<string[]>([]);
    const [historyTrainer, setHistoryTrainer] = useState('전체');
    const [historyStartDate, setHistoryStartDate] = useState('');
    const [historyEndDate, setHistoryEndDate] = useState('');
    const [historyPage, setHistoryPage] = useState(1);
    const [recipientSearch, setRecipientSearch] = useState('');
    const [sendSegmentKey, setSendSegmentKey] = useState<SegmentKey>('rereg');
    const [message, setMessage] = useState(MESSAGE_TEMPLATES['감사']);
    const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
    // 에디터의 조건 선택값 — 발송 시점(조건별 자동) 표시를 위해 controlled 로 둔다
    const [editorTriggerType, setEditorTriggerType] = useState<AutomationTriggerType>('birthday');
    // 실행 이력을 펼친 규칙 id (DX-07)
    const [historyRuleId, setHistoryRuleId] = useState<string | null>(null);

    // 서버 데이터 시딩 — 도착 전에는 빈 상태(로딩/에러 배너로 안내).
    // 참조를 고정해야 candidates에 의존하는 useMemo·useEffect가 매 렌더 재실행되지 않는다.
    const candidates: (Candidate & { memberId?: string })[] = useMemo(
        () => serverData.candidates ?? [],
        [serverData.candidates],
    );
    // '직접 선택'은 세그먼트 후보가 아니라 센터 전체 회원에서 고른다 — 보내기 탭의 직접 선택일 때만 조회
    const centerMemberQuery = usePublicMemberListAll({
        centerId,
        enabled: selectedTab === 'send' && sendTargetMode === 'direct',
    });
    const centerMemberList = centerMemberQuery.data?.results;
    useEffect(() => {
        if (serverData.pointBalance !== null) setPointBalance(serverData.pointBalance);
        if (serverData.unconfirmedAmount !== null) setUnconfirmedAmount(serverData.unconfirmedAmount);
    }, [serverData.pointBalance, serverData.unconfirmedAmount]);
    useEffect(() => {
        if (serverData.chargeRecords) setChargeRecords(serverData.chargeRecords);
    }, [serverData.chargeRecords]);
    useEffect(() => {
        if (serverData.segments) setSegmentList(serverData.segments);
    }, [serverData.segments]);
    useEffect(() => {
        if (serverData.homeSegmentKeys) setHomeSegments(serverData.homeSegmentKeys);
    }, [serverData.homeSegmentKeys]);
    useEffect(() => {
        if (serverData.trainers) setTrainers(serverData.trainers);
    }, [serverData.trainers]);
    trainersRef.current = trainers;
    useEffect(() => {
        if (serverData.automationRules) setAutomationRules(serverData.automationRules);
    }, [serverData.automationRules]);
    useEffect(() => {
        if (serverData.automationLimit !== null) setAutoLimit(serverData.automationLimit);
    }, [serverData.automationLimit]);
    useEffect(() => {
        if (serverData.heldAutomation) setHeldAutomation(serverData.heldAutomation);
    }, [serverData.heldAutomation]);
    useEffect(() => {
        if (serverData.historyRows) setHistoryRows(serverData.historyRows);
    }, [serverData.historyRows]);
    // 후보 목록이 갱신되면 더 이상 존재하지 않는 id 선택을 정리한다 (없는 대상에게 발송 시도 방지)
    useEffect(() => {
        if (!serverData.candidates) return;
        const availableIds = new Set(serverData.candidates.map((candidate) => candidate.id));
        setHomeSelectedIds((prev) => {
            const next = prev.filter((id) => availableIds.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [serverData.candidates]);
    // 회원 목록이 갱신되면 사라진 memberId 선택을 정리한다
    useEffect(() => {
        if (!centerMemberList) return;
        const availableIds = new Set(centerMemberList.map((member) => member.memberId));
        setDirectSelectedMemberIds((prev) => {
            const next = prev.filter((id) => availableIds.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, [centerMemberList]);
    // 디바운스 저장 타이머는 언마운트 시 정리 — 사라진 화면에서 PUT이 날아가지 않게 한다
    useEffect(() => () => {
        Object.values(limitSaveTimers.current).forEach((timer) => clearTimeout(timer));
        if (autoLimitSaveTimer.current) clearTimeout(autoLimitSaveTimer.current);
    }, []);

    const selectedSegments = useMemo(
        () => segmentList.filter((segment) => homeSegments.includes(segment.key)),
        [homeSegments, segmentList],
    );
    // 타이밍보드 각주·대상 목록 배너용 산출 시각 — 스냅샷 일괄 산출이라 아무 세그먼트 값이나 동일
    const segmentComputedAtLabel = useMemo(
        () => formatComputedAt(segmentList.find((segment) => segment.computedAt)?.computedAt),
        [segmentList],
    );
    // 보내기 탭 상품은 전부 서버 목록에서 온다 — 퀵픽은 추천(isFeatured) 상품 우선, 없으면 앞에서부터
    const serverProducts = useMemo(() => serverData.products ?? [], [serverData.products]);
    const quickPickProducts = useMemo(
        () => [
            ...serverProducts.filter((product) => product.isFeatured),
            ...serverProducts.filter((product) => !product.isFeatured),
        ].slice(0, 4),
        [serverProducts],
    );
    const selectedProduct: SendProduct | null = customProduct
        ? toSendProduct(customProduct)
        : (() => {
            const picked = quickPickProducts.find((product) => product.productId === selectedProductId)
                ?? quickPickProducts[0];
            return picked ? toSendProduct(picked) : null;
        })();
    const selectedHomeCandidateCount = candidates.filter((candidate) => homeSegments.includes(candidate.segment)).length;
    // 세그먼트는 서버에서 오므로 도착 전에는 undefined일 수 있다
    const selectedSendSegment: Segment | undefined = segmentList.find((segment) => segment.key === sendSegmentKey) ?? segmentList[0];
    // 후보(선물 타이밍 해당자)를 memberId로 찾아 직접 선택 목록에 사유 배지를 겹쳐 보여준다
    const candidateByMemberId = useMemo(() => {
        const map = new Map<string, Candidate>();
        candidates.forEach((candidate) => {
            if (candidate.memberId) map.set(candidate.memberId, candidate);
        });
        return map;
    }, [candidates]);
    const directMemberRows: DirectMemberRow[] = useMemo(
        () => (centerMemberList ?? []).map((member) => ({
            memberId: member.memberId,
            name: member.name,
            coach: member.coachName?.trim() || NO_COACH_GROUP,
            statusLabel: memberStatusLabel(member),
            hasPhone: !!member.phoneNumber?.trim(),
            candidateDetail: candidateByMemberId.get(member.memberId)?.detail ?? null,
        })),
        [candidateByMemberId, centerMemberList],
    );
    // 선택은 검색 결과가 아니라 전체 목록 기준으로 되살린다 — 검색어를 바꿔도 선택이 사라지지 않게
    const directRecipients = useMemo(
        () => directMemberRows.filter((row) => directSelectedMemberIds.includes(row.memberId)),
        [directMemberRows, directSelectedMemberIds],
    );
    // 담당 강사 목록은 회원 데이터에서 직접 뽑는다 — 하드코딩하면 서버 강사명과 어긋나 대상이 통째로 사라진다
    const directRecipientGroups = useMemo(() => {
        const keyword = recipientSearch.trim();
        const groups = new Map<string, DirectMemberRow[]>();

        directMemberRows.forEach((row) => {
            if (keyword && !row.name.includes(keyword)) return;
            const bucket = groups.get(row.coach);
            if (bucket) bucket.push(row);
            else groups.set(row.coach, [row]);
        });

        return Array.from(groups, ([coach, members]) => ({ coach, members }));
    }, [directMemberRows, recipientSearch]);
    // '추천 대상' 모드의 대상 수는 실제로 발송 가능한 후보 수와 같아야 한다 (세그먼트 집계치 ≠ 발송 대상)
    const recommendedRecipients = candidates.filter((candidate) => candidate.segment === sendSegmentKey);
    const sendRecipientCountByMode: Record<SendTargetMode, number> = {
        direct: directRecipients.length + selectedCoachNames.length,
        recommended: recommendedRecipients.length,
    };
    const selectedSendRecipientCount = sendRecipientCountByMode[sendTargetMode];
    const sendInsufficientPoints = selectedPayment === 'balance' && selectedSendRecipientCount > 0
        && (selectedProduct?.price ?? 0) * selectedSendRecipientCount > pointBalance;
    const visibleCandidates = candidates.filter((candidate) => {
        if (!homeSegments.includes(candidate.segment)) return false;
        if (selectedCandidateTiming === 'all') return true;
        return candidate.segment === selectedCandidateTiming;
    });
    const activeAutomationRules = automationRules.filter((rule) => rule.enabled);
    const automationEstimateCount = activeAutomationRules.reduce((sum, rule) => sum + rule.estimate, 0);
    const automationEstimateCost = activeAutomationRules.reduce((sum, rule) => sum + rule.estimate * rule.price, 0);
    const autoSpent = serverData.automationUsed ?? 0;
    const autoSpentRate = autoLimit > 0 ? Math.min(100, Math.round((autoSpent / autoLimit) * 100)) : 100;
    const editingRule = automationRules.find((rule) => rule.id === editingRuleId) ?? null;
    const enabledTrainerCount = trainers.filter((trainer) => trainer.budgetEnabled).length;
    // 기간 필터는 상대 라벨이 아니라 실제 발송 시각(sortAt)과 경계 시각을 직접 비교한다
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const periodStart = historyPeriod === 'today' ? startOfToday.getTime()
        : historyPeriod === 'week' ? startOfToday.getTime() - 7 * 24 * 60 * 60 * 1000
        : historyPeriod === 'month' ? new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1).getTime()
        : null;
    const filteredHistories = historyRows
        .filter((history) => {
            if (history.status === '예약 대기') return false;
            const sortDate = history.sortAt.slice(0, 10);
            const matchesPeriod = historyPeriod === 'all'
                || (historyPeriod === 'custom'
                    ? (!historyStartDate || sortDate >= historyStartDate) && (!historyEndDate || sortDate <= historyEndDate)
                    : periodStart === null || new Date(history.sortAt).getTime() >= periodStart);
            const matchesType = historyType === '전체' || history.receiverType === historyType;
            const matchesRoute = historyRoute === '전체' || history.route === historyRoute;
            const matchesTrainer = historyTrainer === '전체' || history.trainerName === historyTrainer;
            const matchesKeyword = history.receiver.includes(historyKeyword.trim());
            return matchesPeriod && matchesType && matchesRoute && matchesTrainer && matchesKeyword;
        })
        .sort((a, b) => b.sortAt.localeCompare(a.sortAt));
    const historyTrainerOptions = Array.from(new Set([
        ...historyRows.map((history) => history.trainerName).filter((name): name is string => Boolean(name)),
        ...(historyTrainer !== '전체' ? [historyTrainer] : []),
    ]));
    const historyPageSize = 10;
    const totalHistoryPages = Math.max(1, Math.ceil(filteredHistories.length / historyPageSize));
    const currentHistoryPage = Math.min(historyPage, totalHistoryPages);
    const pagedHistories = filteredHistories.slice((currentHistoryPage - 1) * historyPageSize, currentHistoryPage * historyPageSize);
    const upcomingHistories = historyRows.filter((history) => history.status === '예약 대기');
    const recentHistories = historyRows.filter((history) => history.status !== '예약 대기').slice(0, 4);
    const failedHistoryCount = historyRows.filter((history) => history.status === '발급 실패').length;
    // 상단 통계는 서버 집계(GET /gifts summary)를 그대로 쓴다 — 로컬 100행 재계산은 전체 기간을 대표하지 못한다
    const historySummary = serverData.historySummary;
    // 내역은 최근 100건만 받아온다 — 서버 총 건수가 더 많으면 표에 안내를 띄운다
    const isHistoryTruncated = (historySummary?.count ?? 0) > historyRows.length;

    /**
     * 충전 신청 — 서버 응답으로만 성공을 판정한다.
     * 잔액·충전 내역은 낙관적으로 올리지 않는다 (백엔드가 즉시 지급하므로 invalidation 재조회로 충분).
     */
    const completeCharge = async (charge: { depositorName: string; grantedAmount: number }): Promise<boolean> => {
        try {
            await createChargeMutation.mutateAsync({
                pointAmount: charge.grantedAmount,
                depositorName: charge.depositorName,
                consentTermsVersion: serverData.chargePolicy?.consentTermsVersion ?? 'v1',
            });
            return true;
        } catch (error) {
            const parsed = parseGiftError(error);
            window.alert(parsed?.message ?? '충전 신청 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
            return false;
        }
    };

    /**
     * 발송 확정 — 서버 응답으로만 성공/실패를 판정한다 (낙관적 내역·잔액 반영 금지).
     *   충전 잔액: 그룹(상품)별 POST /gifts(POINT) 후 전 그룹 성공 시에만 resolve.
     *   바로 결제: POST /gifts(DIRECT_CHECKOUT) → 토스 결제창 리다이렉트. 복귀 후 별도 이펙트가 confirm.
     *   실패는 throw — SendConfirmModal이 에러를 표시하고 성공 화면으로 넘어가지 않는다.
     */
    const confirmSend = async (payload: SendConfirmResult): Promise<void | { queued?: boolean }> => {
        if (payload.recipients.length > 100) {
            throw new Error('한 번에 최대 100명까지 보낼 수 있어요. 대상을 나눠서 보내주세요.');
        }
        const routeMap: Record<string, 'DIRECT' | 'DASHBOARD' | 'CAMPAIGN'> = {
            '직접 발송': 'DIRECT',
            '대시보드 추천': 'DASHBOARD',
            '단체 캠페인': 'CAMPAIGN',
        };
        const apiRoute = routeMap[payload.route];
        if (!apiRoute) throw new Error('지원하지 않는 발송 경로예요.');

        // 그룹별 수신자·상품 해석 — 하나라도 해석에 실패하면 발송하지 않고 사유를 알린다 (무음 스킵 금지)
        const problems: string[] = [];
        const requests = (sendPayload?.giftGroups ?? [])
            .map((group) => {
                const groupRecipients = payload.recipients.filter((recipient) => recipient.groupKey === group.key);
                if (groupRecipients.length === 0) return null;
                const recipientRefs = groupRecipients.map((recipient) => (recipient.trainerId
                    ? { type: 'TRAINER' as const, id: recipient.trainerId }
                    : recipient.memberId
                        ? { type: 'MEMBER' as const, id: recipient.memberId }
                        : null));
                if (recipientRefs.some((ref) => ref === null)) {
                    problems.push(`${group.label}: 회원 정보가 연동되지 않은 수신자가 있어요.`);
                    return null;
                }
                // 상품은 선택 시점에 서버 productId를 그대로 실어 왔다 — 이름 대조 없음
                const productId = group.product.id;
                if (!productId) {
                    problems.push(`${group.product.brand} ${group.product.name}: 서버 상품 정보를 찾을 수 없어요.`);
                    return null;
                }
                return {
                    groupKey: group.key,
                    productId,
                    recipients: recipientRefs as { type: 'MEMBER' | 'TRAINER'; id: string }[],
                };
            })
            .filter((request): request is { groupKey: string; productId: string; recipients: { type: 'MEMBER' | 'TRAINER'; id: string }[] } => request !== null);

        if (problems.length > 0) throw new Error(problems.join('\n'));
        if (requests.length === 0) throw new Error('발송할 수 있는 수신자가 없어요.');

        // ── 바로 결제(토스 일반결제): 주문 생성 후 결제창으로 리다이렉트 ──
        if (payload.payment === '바로 결제') {
            if (requests.length > 1) throw new Error('바로 결제는 한 번에 한 가지 상품만 보낼 수 있어요.');
            const [request] = requests;
            let response: any;
            try {
                response = await sendGiftMutation.mutateAsync({
                    recipients: request.recipients,
                    productId: request.productId,
                    message: payload.message,
                    payment: 'DIRECT_CHECKOUT',
                    route: apiRoute,
                    reason: payload.reason,
                    idempotencyKey: `${payload.idempotencyBaseKey}:checkout`,
                });
            } catch (error) {
                const parsed = parseGiftError(error);
                throw new Error(parsed?.message || '결제 주문 생성에 실패했어요. 잠시 후 다시 시도해 주세요.');
            }
            const orderId: string | undefined = response?.orderId;
            const finalAmount: number | undefined = response?.amounts?.final;
            if (!response?.batchId || !orderId || !finalAmount) {
                throw new Error('결제 주문 정보를 받지 못했어요. 잠시 후 다시 시도해 주세요.');
            }
            // 결제 금액은 서버 스냅샷(amounts.final) — 화면 추정치와 원 단위 반올림이 다를 수 있다
            try {
                await requestGiftBatchCheckout({
                    batchId: response.batchId,
                    orderId,
                    orderName: payload.productLabel,
                    amount: finalAmount,
                });
            } catch (checkoutError: any) {
                // 결제창 닫기·SDK 실패 — 방금 만든 결제 대기 배치를 정리해 유령 행을 남기지 않는다 (실패해도 무시:
                //   내역·통계는 서버에서 PENDING_PAYMENT 를 제외하므로 노출되지 않는다)
                cancelGiftBatchMutation.mutate(response.batchId, { onError: () => undefined });
                throw new Error(checkoutError?.message || '결제가 진행되지 않았어요. 다시 시도해 주세요.');
            }
            return; // 토스 결제창으로 리다이렉트됨 — 복귀 후 giftCheckout 이펙트가 승인을 확정한다
        }

        // ── 충전 잔액(POINT): 전 그룹 서버 확정 후에만 성공 처리 ──
        const results = await Promise.allSettled(requests.map((request) => sendGiftMutation.mutateAsync({
            recipients: request.recipients,
            productId: request.productId,
            message: payload.message,
            payment: 'POINT',
            route: apiRoute,
            reason: payload.reason,
            // 그룹별 파생 키 — 같은 모달 안 재시도는 같은 키라 서버가 기존 배치를 재사용(이중 발송 차단).
            // 🔴 **그룹 + 상품** 키다. 상품만 쓰면 두 세그먼트가 같은 상품일 때(추천 상품이 없으면 첫 상품으로
            // 폴백한다) 키가 겹치고, 서버 지문은 센터·상품·결제수단만 비교하므로 두 번째 그룹이 첫 그룹
            // 배치를 idempotentReplay 로 받아 **보내지 않았는데 성공으로 보였다.**
            // 상품도 남기는 이유: 확인 화면에서 그룹 상품을 바꾸고 다시 보내면 새 요청이어야 한다
            // (그룹 키만 쓰면 서버가 '같은 키, 다른 상품' 으로 409 를 낸다).
            idempotencyKey: buildGroupSendIdempotencyKey(payload.idempotencyBaseKey, request.groupKey, request.productId),
        })));
        const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
        if (failures.length > 0) {
            const parsed = parseGiftError(failures[0].reason);
            if (parsed?.errorCode === 'GIFT_INSUFFICIENT_BALANCE') {
                setIsChargeDrawerOpen(true);
                throw new Error('충전 잔액이 부족해요. 충전 후 다시 시도해 주세요.');
            }
            const succeeded = results.length - failures.length;
            throw new Error(
                parsed?.message
                    ? `${parsed.message}${succeeded > 0 ? ` (${succeeded}개 상품은 발송됐어요 — 발송 내역을 확인해 주세요)` : ''}`
                    : '발송에 실패했어요. 발송 내역에서 상태를 확인해 주세요.',
            );
        }
        // 성공 — 내역·잔액은 invalidateQueries가 서버 실값으로 재시딩한다
        // 대량 건(서버가 백그라운드 발급으로 전환)이 하나라도 있으면 모달이 "발송 시작" 문구로 안내
        const queued = results.some(
            (result) => result.status === 'fulfilled' && !!(result.value as any)?.queued,
        );
        return queued ? { queued: true } : undefined;
    };

    const openTimingSend = useCallback((targets: (Candidate & { memberId?: string })[]) => {
        if (targets.length === 0) return;
        if (targets.length > 100) {
            window.alert(`한 번에 최대 100명까지 보낼 수 있어요. 현재 대상이 ${targets.length}명이에요 — 상황별로 나눠서 보내주세요.`);
            return;
        }
        if (serverProducts.length === 0) {
            window.alert('상품 정보를 아직 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
            return;
        }
        const targetSegmentKeys = Array.from(new Set(targets.map((target) => target.segment)));
        const giftGroups = targetSegmentKeys.map((segmentKey) => {
            const segment = segmentList.find((item) => item.key === segmentKey);
            // ① 서버 추천 상품 → ② 세그먼트 단가와 같은 서버 상품 → ③ 서버 상품 첫 번째
            const priceMatch = segment ? serverProducts.find((item) => item.price === segment.unitPrice) : undefined;
            const product = segment?.recommendedProduct ?? toSendProduct(priceMatch ?? serverProducts[0]);
            return { key: segmentKey, label: segment?.title ?? '선물 대상', product };
        });
        setSendPayload({
            recipients: targets.map((target) => ({
                memberId: target.memberId,
                name: target.name,
                groupKey: target.segment,
                reason: target.detail,
                trainer: target.coach,
                detail: target.lastGift === '없음' ? '최근 선물 없음' : `최근 선물 ${target.lastGift}`,
            })),
            giftGroups,
            payment: '충전 잔액',
            message: MESSAGE_TEMPLATES['감사'],
            reason: '선물 타이밍 추천',
            route: '대시보드 추천',
            recipientType: '회원',
        });
    }, [segmentList, serverProducts]);

    useEffect(() => {
        if (dashboardActionHandled.current || searchParams.get('action') !== 'send-timing-all') return;
        // 상품이 아직 로딩 중이면 ref를 소진하지 않고 기다린다 — 지금 열면 상품 미도착 안내만 뜬다
        if (serverProducts.length === 0 && serverData.productsStatus.isLoading) return;

        const targets = candidates.filter((candidate) => homeSegments.includes(candidate.segment));
        if (targets.length === 0) {
            // 후보가 아직 안 왔을 수 있으므로 로딩 중에는 ref를 소진하지 않고 다음 렌더에 다시 시도한다
            if (serverData.isLoading) return;
            dashboardActionHandled.current = true;
            setSelectedTab('home');
            window.alert('지금 챙길 대상이 없어요.');
            return;
        }

        dashboardActionHandled.current = true;
        setSelectedTab('home');
        setSelectedCandidateTiming('all');
        openTimingSend(targets);
    }, [candidates, homeSegments, openTimingSend, searchParams, serverData.isLoading, serverData.productsStatus.isLoading, serverProducts]);

    // 토스 결제창 복귀 처리 — successUrl/failUrl 이 /gifticon?giftCheckout=... 으로 돌아온다.
    //   성공 복귀 시 서버 confirm(승인+발급)을 호출해야 결제가 확정된다.
    const checkoutReturnHandled = useRef(false);
    useEffect(() => {
        if (checkoutReturnHandled.current) return;
        const checkoutResult = searchParams.get(GIFT_CHECKOUT_QUERY_KEY);
        if (!checkoutResult) return;
        checkoutReturnHandled.current = true;

        const batchId = searchParams.get(GIFT_CHECKOUT_BATCH_KEY);
        const paymentKey = searchParams.get('paymentKey');
        const orderId = searchParams.get('orderId');
        const amount = Number(searchParams.get('amount'));
        const tossFailMessage = searchParams.get('message');
        // 새로고침 시 confirm 재실행을 막기 위해 쿼리를 즉시 정리 (승인 자체는 서버 멱등 처리)
        window.history.replaceState(null, '', '/gifticon');

        if (checkoutResult !== 'success') {
            // 작업 맥락(보내기 탭)으로 복귀 — 홈으로 떨어지면 관리자가 처음부터 다시 골라야 한다
            setSelectedTab('send');
            window.alert(tossFailMessage || '결제가 완료되지 않았어요. 카드 결제는 진행되지 않았으니 다시 시도해 주세요.');
            return;
        }
        if (!batchId || !paymentKey || !orderId || !Number.isFinite(amount) || amount <= 0) {
            window.alert('결제 정보를 확인할 수 없어요. 발송 내역에서 결제 상태를 확인해 주세요.');
            return;
        }
        confirmGiftPaymentMutation.mutate(
            { batchId, request: { paymentKey, orderId, amount } },
            {
                onSuccess: (data) => {
                    if (data.issuing) {
                        // 대량 건 — 결제는 확정, 발급은 백그라운드 진행
                        window.alert('결제가 완료됐어요. 인원이 많아 기프티콘 발급이 순차 진행 중이에요 — 발송 내역에서 확인할 수 있어요.');
                        setSelectedTab('history');
                        return;
                    }
                    if (data.successCount > 0 && data.failedCount > 0) {
                        window.alert(`결제는 완료됐지만 ${data.failedCount}건은 발급에 실패해 보류함으로 이동했어요.`);
                    } else if (data.successCount === 0 && data.failedCount > 0) {
                        window.alert('기프티콘 발급에 실패해 결제가 자동 취소되었어요. 잠시 후 다시 시도해 주세요.');
                    } else {
                        window.alert('결제가 완료되어 선물이 발송되었어요.');
                    }
                    setSelectedTab('history');
                },
                onError: (error) => {
                    const parsed = parseGiftError(error);
                    window.alert(parsed?.message || '결제 승인에 실패했어요. 카드가 결제되었다면 발송 내역에서 상태를 확인해 주세요.');
                },
            },
        );
    }, [searchParams, confirmGiftPaymentMutation]);

    // 결제 승인·발급 진행 중 이탈 방지 — confirm 은 수신자 수만큼 다우 발급을 도는 동기 요청이라 수 초 이상 걸린다
    const isConfirmingPayment = confirmGiftPaymentMutation.isLoading;
    useEffect(() => {
        if (!isConfirmingPayment) return undefined;
        const guard = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            // eslint-disable-next-line no-param-reassign
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', guard);
        return () => window.removeEventListener('beforeunload', guard);
    }, [isConfirmingPayment]);

    const openSendConfirm = () => {
        const count = sendRecipientCountByMode[sendTargetMode];
        if (count === 0) return;
        if (count > 100) {
            window.alert(`한 번에 최대 100명까지 보낼 수 있어요. 현재 ${count}명이 선택되어 있어요 — 대상을 나눠서 보내주세요.`);
            return;
        }
        if (!selectedProduct) {
            window.alert('상품 정보를 아직 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
            return;
        }
        const coachRecipients: SendRecipient[] = sendTargetMode === 'direct'
            ? selectedCoachNames.map((name) => ({
                trainerId: trainers.find((trainer) => trainer.name === name)?.id,
                name,
                groupKey: 'all',
                reason: '강사 발송',
                detail: '담당 강사',
            }))
            : [];
        // 수신자는 실제 회원/후보/강사에서만 만든다 — 자리 채우기용 가짜 수신자는 만들지 않는다
        const memberRecipients: SendRecipient[] = sendTargetMode === 'direct'
            ? directRecipients.map((row) => ({
                memberId: row.memberId,
                name: row.name,
                groupKey: 'all',
                // 타이밍 후보와 겹치는 회원은 기존 사유를 그대로 유지한다
                reason: row.candidateDetail ?? '직접 선택',
                trainer: row.coach === NO_COACH_GROUP ? undefined : row.coach,
                detail: row.candidateDetail ?? row.statusLabel ?? '직접 선택',
            }))
            : recommendedRecipients.map((candidate) => ({
                memberId: candidate.memberId,
                name: candidate.name,
                groupKey: 'all',
                reason: candidate.detail,
                trainer: candidate.coach,
                detail: candidate.lastGift === '없음' ? '최근 선물 없음' : `최근 선물 ${candidate.lastGift}`,
            }));
        const recipients: SendRecipient[] = [...coachRecipients, ...memberRecipients];
        setSendPayload({
            recipients,
            giftGroups: [{ key: 'all', label: count > 1 ? `선택 대상 ${count}명` : '선택 대상', product: selectedProduct }],
            payment: selectedPayment === 'card' ? '바로 결제' : '충전 잔액',
            message,
            reason: sendTargetMode === 'direct' ? '직접 발송' : `${selectedSendSegment?.title ?? '추천 대상'} 추천 발송`,
            route: sendTargetMode === 'direct' ? '직접 발송' : '대시보드 추천',
            recipientType: coachRecipients.length > 0 && directRecipients.length === 0 ? '강사' : '회원',
        });
    };

    /** 뮤테이션 실패 안내 — 서버 메시지를 우선 노출하고 없으면 기본 문구를 쓴다 */
    const alertMutationError = (error: unknown, fallback: string) => {
        const parsed = parseGiftError(error);
        window.alert(parsed?.message || fallback);
    };

    const saveHomeSegments = (next: SegmentKey[]) => {
        const previous = homeSegments;
        setHomeSegments(next);
        updateSettingsMutation.mutate({ homeSegmentKeys: next.map(toApiSegmentKey) }, {
            onError: (error) => {
                setHomeSegments(previous);
                alertMutationError(error, '홈 선물 타이밍 설정을 저장하지 못했어요.');
            },
        });
    };

    const toggleHomeSegment = (key: SegmentKey) => {
        const next = homeSegments.includes(key) ? homeSegments.filter((item) => item !== key) : [...homeSegments, key];
        if (homeSegments.includes(key) && selectedCandidateTiming === key) setSelectedCandidateTiming('all');
        saveHomeSegments(next);
    };

    /**
     * @param nextLimit 입력이 비어 있으면 `null`.
     *
     * 예전에는 한도를 전부 지우면 `Number('') === 0` 이 되어 **한도 0P 가 그대로 자동 저장**됐다.
     * 빈칸일 때는 저장 타이머를 걸지 않고, 대기 중이던 저장도 취소한다. 저장을 하지 않으므로
     * 로컬 값이 서버와 어긋난 채 남지 않게 서버 실값으로 되돌려 두고 화면만 비운다.
     */
    const changeAutoLimit = (nextLimit: number | null) => {
        const previous = autoLimit;
        if (autoLimitSaveTimer.current) clearTimeout(autoLimitSaveTimer.current);
        if (nextLimit === null) {
            const restored = serverData.automationLimit ?? previous;
            setAutoLimit(restored);
            setAutoLimitBlankAt(restored);
            return;
        }
        setAutoLimitBlankAt(null);
        setAutoLimit(nextLimit);
        autoLimitSaveTimer.current = setTimeout(() => {
            updateAutomationLimitMutation.mutate({ monthlyLimit: nextLimit }, {
                onError: (error) => {
                    // 저장 실패 시에는 서버 실값으로 되돌린다 (서버 값이 아직 없으면 직전 로컬 값)
                    // 값을 갈아끼우므로 낡은 커서 복원과 빈칸 표시를 함께 버린다.
                    amountCaret.dropPendingCaret();
                    setAutoLimitBlankAt(null);
                    setAutoLimit(serverData.automationLimit ?? previous);
                    alertMutationError(error, '자동 발송 월 한도를 저장하지 못했어요.');
                },
            });
        }, 600);
    };

    /** 강사 권한·한도 저장 실패 롤백 — 배열 전체 스냅샷 대신 해당 강사만 서버 실값으로 되돌린다 */
    const rollbackTrainer = (trainerId: string) => {
        const serverTrainer = serverData.trainers?.find((item) => item.id === trainerId);
        if (!serverTrainer) return;
        setTrainers((prev) => prev.map((item) => item.id === trainerId
            ? { ...item, budgetEnabled: serverTrainer.budgetEnabled, monthlyLimit: serverTrainer.monthlyLimit }
            : item));
    };

    const renderHome = () => (
        <div className="space-y-[18px]">
            {heldAutomation.length > 0 && (
                <button
                    type="button"
                    onClick={() => setSelectedTab('automation')}
                    className="flex w-full items-center gap-[9px] rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] px-[16px] py-[13px] text-left hover:bg-[#FEF3C7]"
                >
                    <AlertCircle size={18} color="#B45309" className="shrink-0" />
                    <p className="text-[13px] font-bold leading-[20px] text-[#92400E]">보류함에 {heldAutomation.length}건이 있어요 — 자동화 탭에서 재발송할 수 있어요</p>
                </button>
            )}
            <div className="grid grid-cols-4 gap-[12px]">
                <InsightCard
                    title="오늘 챙길 대상"
                    value={`${selectedHomeCandidateCount}명`}
                    desc={`선택한 선물 타이밍 기준 대기 인원입니다.`}
                    tone="blue"
                    onClick={() => document.getElementById('gift-candidates')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                />
                {/* 자동 발송 실행이 동작하므로 카드 복원 (U-05 롤백) */}
                <InsightCard
                    title="이번 달 자동 발송"
                    value={`${serverData.summary?.autoScheduled.count ?? 0}건`}
                    desc={<>켜진 규칙 {serverData.summary?.autoScheduled.activeRuleCount ?? 0}개 · {point(serverData.summary?.autoScheduled.amount ?? 0)}</>}
                    tone="orange"
                    onClick={() => setSelectedTab('automation')}
                />
                <InsightCard
                    title="이번 달 발송"
                    value={`${serverData.summary?.monthSentCount ?? 0}건`}
                    desc={`누적 발송 선물 ${(serverData.summary?.totalSentCount ?? 0).toLocaleString()}건`}
                    tone="green"
                    onClick={() => setSelectedTab('history')}
                />
                <InsightCard
                    title="선물 받은 회원"
                    value={`${serverData.summary?.giftedMembers.total ?? 0}명`}
                    desc={<>누적 기준 · 이 중 재등록 <b className="text-[#BE185D]">{serverData.summary?.giftedMembers.reregistered ?? 0}명</b></>}
                    tone="pink"
                    onClick={() => setSelectedTab('history')}
                />
            </div>

            <div className="grid grid-cols-[1.1fr_1fr] gap-[18px]">
                <Card className="p-[22px]">
                    <div className="flex items-start justify-between gap-[16px]">
                        <SectionTitle
                            title={`선물 타이밍 보드 (${selectedSegments.length}개 표시 중)`}
                            description="생일, 재등록, 미출석처럼 놓치기 쉬운 순간을 선택해 꾸준히 관리하세요."
                        />
                        <button
                            type="button"
                            onClick={() => setIsSegmentSettingOpen(true)}
                            className="inline-flex h-[38px] items-center gap-[7px] rounded-[8px] border border-[#CBD5E1] bg-white px-[12px] text-[13px] font-bold text-[#334155] hover:bg-[#F8FAFC]"
                        >
                            <Settings2 size={16} />
                            설정
                        </button>
                    </div>

                    <div className="gift-scroll mt-[18px] grid max-h-[440px] grid-cols-2 gap-[12px] pr-[4px]">
                        {selectedSegments.map((segment) => (
                            <div key={segment.key} className="rounded-[8px] border border-[#E2E8F0] p-[14px]">
                                <div className="flex items-start justify-between gap-[12px]">
                                    <div>
                                        <div className="flex items-center gap-[8px]">
                                            <span className="h-[10px] w-[10px] rounded-full" style={{ backgroundColor: segment.tone }} />
                                            <b className="text-[15px] text-[#1F2937]">{segment.title}</b>
                                            {!!segment.criteria && (
                                                <span className="group relative inline-flex cursor-help items-center">
                                                    <Info size={13} color="#94A3B8" />
                                                    <span className="pointer-events-none absolute left-1/2 top-[20px] z-20 hidden w-[250px] -translate-x-1/2 rounded-[8px] bg-[#1F2937] px-[11px] py-[9px] text-[11px] font-normal leading-[17px] text-white shadow-lg group-hover:block">
                                                        {segment.criteria}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-[4px] text-[12px] leading-[18px] text-[#64748B]">{segment.description}</p>
                                    </div>
                                    <Badge tone={segment.priority === '높음' ? 'orange' : 'gray'}>{segment.priority}</Badge>
                                </div>

                                <div className="mt-[14px] grid grid-cols-3 gap-[8px]">
                                    <div>
                                        <p className="text-[11px] text-[#94A3B8]">대기</p>
                                        <p className="text-[18px] font-bold text-[#111827]">{segment.count}명</p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-[#94A3B8]">이번 달</p>
                                        <p className="text-[18px] font-bold text-[#111827]">{segment.sentThisMonth}건</p>
                                    </div>
                                    <div>
                                        <p className="text-[11px] text-[#94A3B8]">사용액</p>
                                        <p className="text-[18px] font-bold text-[#111827]">{point(segment.budgetUsed)}</p>
                                    </div>
                                </div>

                                <div className="mt-[14px] flex items-center justify-between rounded-[8px] bg-[#F8FAFC] px-[12px] py-[10px]">
                                    <div>
                                        <p className="text-[11px] text-[#94A3B8]">추천 선물</p>
                                        <p className="text-[13px] font-bold text-[#334155]">{segment.recommendedGift}</p>
                                    </div>
                                    <p className="text-[13px] font-bold text-[#3D73DD]">{point(segment.unitPrice)}</p>
                                </div>
                            </div>
                        ))}
                        {selectedSegments.length === 0 && (
                            <div className="col-span-2 rounded-[8px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-[14px] py-[28px] text-center text-[13px] text-[#64748B]">
                                {serverData.isLoading ? '선물 타이밍을 불러오는 중입니다…' : '표시할 선물 타이밍이 없습니다. 설정에서 상황을 켜 주세요.'}
                            </div>
                        )}
                    </div>

                    {segmentComputedAtLabel && (
                        <p className="mt-[10px] text-[11px] text-[#94A3B8]">
                            {segmentComputedAtLabel} 기준 · 이번 달 선물을 이미 받은 회원은 제외됩니다
                        </p>
                    )}
                </Card>

                <Card id="gift-candidates" className="p-[22px]">
                    <SectionTitle title="지금 챙기면 좋은 대상" description="선택한 대상들에 맞춰 오늘 확인하면 좋은 회원을 먼저 보여드립니다." />
                    <div className="mt-[14px] flex items-start justify-between gap-[12px]">
                        <div className="flex flex-wrap gap-[6px]">
                            <button
                                type="button"
                                onClick={() => setSelectedCandidateTiming('all')}
                                className={`h-[32px] rounded-full border px-[12px] text-[12px] font-bold ${
                                    selectedCandidateTiming === 'all'
                                        ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]'
                                        : 'border-[#CBD5E1] bg-white text-[#64748B]'
                                }`}
                            >
                                전체 {selectedHomeCandidateCount}
                            </button>
                            {selectedSegments.map((segment) => (
                                <button
                                    key={segment.key}
                                    type="button"
                                    onClick={() => setSelectedCandidateTiming(segment.key)}
                                    className={`h-[32px] rounded-full border px-[12px] text-[12px] font-bold ${
                                        selectedCandidateTiming === segment.key
                                            ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]'
                                            : 'border-[#CBD5E1] bg-white text-[#64748B]'
                                    }`}
                                >
                                    {segment.title} {candidates.filter((candidate) => candidate.segment === segment.key).length}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-shrink-0 gap-[6px]">
                            <button
                                type="button"
                                onClick={() => openTimingSend(visibleCandidates.filter((candidate) => homeSelectedIds.includes(candidate.id)))}
                                className="h-[32px] rounded-[8px] border border-[#CBD5E1] bg-white px-[10px] text-[12px] font-bold text-[#334155] hover:bg-[#F8FAFC]"
                            >
                                선택 보내기 ({visibleCandidates.filter((candidate) => homeSelectedIds.includes(candidate.id)).length})
                            </button>
                            <button
                                type="button"
                                onClick={() => openTimingSend(visibleCandidates)}
                                className="h-[32px] rounded-[8px] bg-[#3D73DD] px-[10px] text-[12px] font-bold text-white"
                            >
                                전체 보내기 ({visibleCandidates.length})
                            </button>
                        </div>
                    </div>
                    {/* 기준 요약 배너(UX 기획 §4-3) — 특정 타이밍 선택 시 그 기준, 전체 탭은 당월 제외 안내 */}
                    <div className="mt-[10px] rounded-[8px] bg-[#F8FAFC] px-[12px] py-[8px] text-[12px] leading-[18px] text-[#64748B]">
                        {(selectedCandidateTiming !== 'all'
                            && segmentList.find((segment) => segment.key === selectedCandidateTiming)?.criteria)
                            || '이번 달 선물을 이미 받은 회원은 제외된 목록입니다.'}
                        {segmentComputedAtLabel && (
                            <span className="text-[#94A3B8]"> · {segmentComputedAtLabel} 기준</span>
                        )}
                    </div>
                    <div className="gift-scroll mt-[12px] max-h-[300px] space-y-[10px] pr-[4px]">
                        {visibleCandidates.map((candidate) => {
                            const segment = segmentList.find((item) => item.key === candidate.segment);
                            const checked = homeSelectedIds.includes(candidate.id);

                            return (
                                <div key={candidate.id} className="flex items-center justify-between gap-[12px] rounded-[8px] border border-[#E2E8F0] px-[12px] py-[10px]">
                                    <label
                                        onClick={() => setHomeSelectedIds((prev) => prev.includes(candidate.id) ? prev.filter((id) => id !== candidate.id) : [...prev, candidate.id])}
                                        className="flex min-w-0 flex-1 cursor-pointer items-center gap-[10px]"
                                    >
                                        <span className={`flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-[6px] border ${checked ? 'border-[#3D73DD] bg-[#3D73DD]' : 'border-[#CBD5E1] bg-white'}`}>
                                            {checked && <Check size={14} color="white" />}
                                            {!checked && <Plus size={13} color="#CBD5E1" />}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-[8px]">
                                                <b className="text-[14px] text-[#1F2937]">{candidate.name}</b>
                                                <Badge tone="blue">{segment?.title}</Badge>
                                            </div>
                                            <p className="mt-[3px] text-[12px] text-[#64748B]">
                                                {candidate.detail} · 담당 {candidate.coach} · 최근 선물 {candidate.lastGift}
                                            </p>
                                        </div>
                                    </label>
                                    <span className="text-[12px] font-bold text-[#64748B]">{segment?.recommendedGift ?? '추천 선물'}</span>
                                </div>
                            );
                        })}
                        {visibleCandidates.length === 0 && (
                            <div className="rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] px-[14px] py-[24px] text-center text-[13px] text-[#64748B]">
                                {serverData.candidatesStatus.isLoading ? '대상을 불러오는 중입니다…'
                                    : serverData.candidatesStatus.isError ? '대상을 불러오지 못했어요.'
                                    : '해당 선물 타이밍에 챙길 대상이 없습니다.'}
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            <Card className="p-[22px]">
                <div className="flex items-start justify-between gap-[16px]">
                    <SectionTitle title="최근 발송 내역" description="최근에 보낸 선물의 대상, 사유, 상태를 빠르게 확인합니다." />
                    <button
                        type="button"
                        onClick={() => setSelectedTab('history')}
                        className="inline-flex h-[36px] items-center gap-[6px] rounded-[8px] border border-[#CBD5E1] bg-white px-[12px] text-[12px] font-bold text-[#334155] hover:bg-[#F8FAFC]"
                    >
                        전체 보기
                        <ChevronRight size={14} />
                    </button>
                </div>
                <div className="mt-[14px] overflow-hidden rounded-[8px] border border-[#E2E8F0]">
                    <div className="grid grid-cols-[120px_1fr_1fr_140px_110px] bg-[#F8FAFC] px-[14px] py-[10px] text-[12px] font-bold text-[#64748B]">
                        <span>일시</span>
                        <span>받는 사람</span>
                        <span>발송 사유</span>
                        <span>상품</span>
                        <span className="text-right">상태</span>
                    </div>
                    {recentHistories.length === 0 && (
                        <p className="border-t border-[#E2E8F0] py-[26px] text-center text-[13px] text-[#94A3B8]">
                            {serverData.historyStatus.isLoading ? '발송 내역을 불러오는 중입니다…' : '아직 발송한 선물이 없습니다.'}
                        </p>
                    )}
                    {recentHistories.map((history) => (
                        <div
                            key={history.id}
                            onClick={() => setHistoryDetail(history)}
                            className="grid cursor-pointer grid-cols-[120px_1fr_1fr_140px_110px] items-center border-t border-[#E2E8F0] px-[14px] py-[12px] text-[13px] text-[#334155] hover:bg-[#FBFCFE]"
                        >
                            <span>{history.date}</span>
                            <b className="text-[#111827]">{history.receiver}</b>
                            <span>{history.reason}</span>
                            <span>{history.product}</span>
                            <span className="text-right">
                                <Badge tone={getHistoryStatusTone(history.displayStatus, history.status)}>{history.status}</Badge>
                            </span>
                        </div>
                    ))}
                </div>
            </Card>

            <div className="grid grid-cols-2 gap-[18px]">
                <Card className="p-[22px]">
                    <div className="flex items-start justify-between gap-[16px]">
                        <SectionTitle title="최근 충전 내역" description="무통장입금 신청과 입금 확인 상태를 확인합니다." />
                        <button
                            type="button"
                            onClick={() => setIsChargeDrawerOpen(true)}
                            className="h-[34px] rounded-[8px] border border-[#CBD5E1] bg-white px-[11px] text-[12px] font-bold text-[#475569] hover:bg-[#F8FAFC]"
                        >
                            충전하기
                        </button>
                    </div>
                    <div className="mt-[14px] space-y-[8px]">
                        {chargeRecords.slice(0, 4).map((record) => (
                            <div key={record.id} className="flex items-center justify-between gap-[12px] rounded-[8px] border border-[#E2E8F0] px-[13px] py-[10px]">
                                <div className="min-w-0">
                                    <p className="truncate text-[13px] font-bold text-[#1F2937]">
                                        {point(record.grantedAmount)}
                                        <span className="ml-[6px] text-[12px] font-normal text-[#64748B]">
                                            {money(record.depositAmount)} 입금{record.discountAmount > 0 ? ` · 할인 ${money(record.discountAmount)}` : ''}
                                        </span>
                                    </p>
                                    <p className="mt-[2px] text-[11px] text-[#94A3B8]">{record.requestedAt} · {record.depositorName}</p>
                                </div>
                                <Badge tone={record.status === '충전 완료' ? 'green' : record.status === '입금 확인 중' ? 'orange' : 'gray'}>{record.status}</Badge>
                            </div>
                        ))}
                        {chargeRecords.length === 0 && (
                            <p className="rounded-[8px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-[13px] py-[22px] text-center text-[13px] text-[#64748B]">
                                {serverData.isLoading ? '충전 내역을 불러오는 중입니다…' : '아직 충전 내역이 없습니다.'}
                            </p>
                        )}
                    </div>
                </Card>

                <Card className="p-[22px]">
                    <div className="flex items-center gap-[8px]">
                        <BarChart3 size={19} color="#3D73DD" />
                        <SectionTitle title="자주 보낸 선물 TOP 5" description="전체 기간 발송 횟수 기준입니다. 다음 선물을 고를 때 참고하세요." />
                    </div>
                    <div className="mt-[14px] space-y-[8px]">
                        {(serverData.topProducts ?? []).map((item) => (
                            <div key={item.rank} className="flex min-w-0 items-center gap-[10px] rounded-[8px] border border-[#E2E8F0] px-[12px] py-[9px]">
                                <span className="w-[18px] shrink-0 text-center text-[13px] font-bold text-[#94A3B8]">{item.rank}</span>
                                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[8px] bg-[#ECF1FC]"><Gift size={16} color="#3D73DD" /></div>
                                <p className="min-w-0 flex-1 truncate text-[12px] font-bold text-[#334155]">{item.name}</p>
                                <p className="shrink-0 text-[11px] text-[#3D73DD]">{item.count}회 발송 · 총 {point(item.totalAmount)}</p>
                            </div>
                        ))}
                        {(serverData.topProducts ?? []).length === 0 && (
                            <p className="rounded-[8px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-[12px] py-[22px] text-center text-[13px] text-[#64748B]">
                                {serverData.isLoading ? '집계를 불러오는 중입니다…' : '아직 집계할 발송 기록이 없습니다.'}
                            </p>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );

    const renderSend = () => (
        <div className="grid grid-cols-[1fr_360px] gap-[18px]">
            <Card className="p-[22px]">
                <SectionTitle title="선물 보내기" description="대상을 고르는 방식만 선택하면 선물, 메시지, 결제 흐름은 동일하게 진행됩니다." />

                <div className="mt-[16px] grid grid-cols-2 gap-[8px]">
                    {([
                        ['direct', '직접 선택', '센터 전체 회원·강사에서 여러 명 선택'],
                        ['recommended', '추천 대상', '생일·재등록 등 자주 쓰는 대상'],
                    ] as [SendTargetMode, string, string][]).map(([mode, label, description]) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => setSendTargetMode(mode)}
                            className={`rounded-[8px] border p-[12px] text-left transition-colors ${
                                sendTargetMode === mode
                                    ? 'border-[#3D73DD] bg-[#ECF1FC]'
                                    : 'border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]'
                            }`}
                        >
                            <p className={`text-[13px] font-bold ${sendTargetMode === mode ? 'text-[#3D73DD]' : 'text-[#1F2937]'}`}>
                                {label}
                            </p>
                            <p className="mt-[3px] text-[11px] leading-[16px] text-[#64748B]">{description}</p>
                        </button>
                    ))}
                </div>

                <div className="mt-[18px] grid grid-cols-2 gap-[18px]">
                    <div>
                        <label className="text-[12px] font-bold text-[#64748B]">누구에게 보낼까요?</label>
                        {sendTargetMode === 'direct' && (
                            <>
                                <div className="mt-[8px] flex h-[42px] items-center gap-[8px] rounded-[8px] border border-[#CBD5E1] bg-white px-[12px] focus-within:border-[#3D73DD]">
                                    <Search size={16} color="#64748B" />
                                    <input
                                        value={recipientSearch}
                                        onChange={(event) => setRecipientSearch(event.target.value)}
                                        className="w-full text-[14px] outline-none"
                                        placeholder="회원 이름 검색"
                                    />
                                </div>
                                <p className="mt-[6px] text-[11px] leading-[16px] text-[#64748B]">센터 전체 회원에서 선택할 수 있어요.</p>
                                <div className="mt-[12px] max-h-[370px] space-y-[10px] overflow-y-auto pr-[4px]">
                                    {directRecipientGroups.length === 0 && (
                                        <div className="rounded-[8px] border border-dashed border-[#CBD5E1] px-[12px] py-[18px] text-center">
                                            <p className="text-[12px] text-[#94A3B8]">
                                                {centerMemberQuery.isLoading ? '회원 목록을 불러오는 중입니다…'
                                                    : centerMemberQuery.isError ? '회원 목록을 불러오지 못했어요.'
                                                    : recipientSearch.trim() ? '검색 결과가 없습니다.'
                                                    : '등록된 회원이 없습니다.'}
                                            </p>
                                            {centerMemberQuery.isError && (
                                                <button
                                                    type="button"
                                                    onClick={() => { void centerMemberQuery.refetch(); }}
                                                    className="mt-[10px] h-[28px] rounded-[7px] border border-[#CBD5E1] bg-white px-[10px] text-[11px] font-bold text-[#475569]"
                                                >
                                                    다시 시도
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {directRecipientGroups.map((group) => {
                                        const groupMemberIds = group.members.map((member) => member.memberId);
                                        const isGroupSelected = groupMemberIds.every((id) => directSelectedMemberIds.includes(id));

                                        return (
                                        <div key={group.coach} className="rounded-[8px] border border-[#E2E8F0]">
                                            <div className="flex items-center justify-between border-b border-[#E2E8F0] bg-[#F8FAFC] px-[12px] py-[9px]">
                                                <div>
                                                    <p className="text-[13px] font-bold text-[#1F2937]">
                                                        {group.coach === NO_COACH_GROUP ? group.coach : `${group.coach} 강사`}
                                                    </p>
                                                    <p className="text-[11px] text-[#64748B]">담당 회원 {group.members.length}명</p>
                                                </div>
                                                <div className="flex items-center gap-[6px]">
                                                    {group.coach !== NO_COACH_GROUP && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setSelectedCoachNames((prev) => prev.includes(group.coach) ? prev.filter((name) => name !== group.coach) : [...prev, group.coach])}
                                                            className={`h-[28px] rounded-[7px] border px-[9px] text-[11px] font-bold ${
                                                                selectedCoachNames.includes(group.coach)
                                                                    ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]'
                                                                    : 'border-[#CBD5E1] bg-white text-[#475569]'
                                                            }`}
                                                        >
                                                            {selectedCoachNames.includes(group.coach) ? '강사 선택됨 ✓' : '강사 선택'}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setDirectSelectedMemberIds((prev) => isGroupSelected
                                                            ? prev.filter((id) => !groupMemberIds.includes(id))
                                                            : Array.from(new Set([...prev, ...groupMemberIds])))}
                                                        className={`h-[28px] rounded-[7px] border px-[9px] text-[11px] font-bold ${
                                                            isGroupSelected
                                                                ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]'
                                                                : 'border-[#CBD5E1] bg-white text-[#475569]'
                                                        }`}
                                                    >
                                                        {isGroupSelected ? '그룹 선택됨 ✓' : '그룹 선택'}
                                                    </button>
                                                </div>
                                            </div>
                                            {group.members.map((row) => {
                                                const checked = directSelectedMemberIds.includes(row.memberId);

                                                return (
                                                    <label
                                                        key={row.memberId}
                                                        onClick={() => setDirectSelectedMemberIds((prev) => prev.includes(row.memberId) ? prev.filter((id) => id !== row.memberId) : [...prev, row.memberId])}
                                                        className="flex cursor-pointer items-center justify-between gap-[8px] px-[12px] py-[9px] hover:bg-[#FBFCFE]"
                                                    >
                                                        <span className="flex min-w-0 flex-wrap items-center gap-[6px]">
                                                            <b className="text-[14px] text-[#1F2937]">{row.name}</b>
                                                            {row.statusLabel && (
                                                                <Badge tone={row.statusLabel === '활성' ? 'green' : row.statusLabel === '만료' ? 'red' : 'orange'}>
                                                                    {row.statusLabel}
                                                                </Badge>
                                                            )}
                                                            {row.candidateDetail && <Badge tone="blue">{row.candidateDetail}</Badge>}
                                                            {!row.hasPhone && <Badge tone="gray">연락처 없음</Badge>}
                                                        </span>
                                                        <span className={`flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[6px] border ${checked ? 'border-[#3D73DD] bg-[#3D73DD]' : 'border-[#CBD5E1] bg-white'}`}>
                                                            {checked && <Check size={14} color="white" />}
                                                            {!checked && <Plus size={13} color="#CBD5E1" />}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {sendTargetMode === 'recommended' && (
                            <div className="mt-[8px] grid grid-cols-2 gap-[8px]">
                                {segmentList.slice(0, 6).map((segment) => (
                                    <button
                                        key={segment.key}
                                        type="button"
                                        onClick={() => setSendSegmentKey(segment.key)}
                                        className={`rounded-[8px] border p-[12px] text-left ${
                                            sendSegmentKey === segment.key ? 'border-[#3D73DD] bg-[#ECF1FC]' : 'border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]'
                                        }`}
                                    >
                                        <p className="text-[13px] font-bold text-[#1F2937]">{segment.title}</p>
                                        <p className="mt-[2px] text-[11px] leading-[16px] text-[#64748B]">{segment.description}</p>
                                        <p className="mt-[10px] text-[16px] font-bold text-[#3D73DD]">{segment.count}명</p>
                                    </button>
                                ))}
                                {segmentList.length === 0 && (
                                    <p className="col-span-2 rounded-[8px] border border-dashed border-[#CBD5E1] px-[12px] py-[18px] text-center text-[12px] text-[#94A3B8]">
                                        {serverData.isLoading ? '추천 대상을 불러오는 중입니다…' : '추천 대상을 불러오지 못했습니다.'}
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="mt-[12px] flex flex-wrap gap-[6px]">
                            {(sendTargetMode === 'direct'
                                ? [...selectedCoachNames.map((name) => `${name} 강사`), ...directRecipients.map((row) => row.name)]
                                : recommendedRecipients.map((candidate) => candidate.name)
                            ).slice(0, 5).map((name) => (
                                <span key={name} className="inline-flex h-[28px] items-center rounded-full bg-[#ECF1FC] px-[10px] text-[12px] font-bold text-[#3D73DD]">
                                    {name}
                                </span>
                            ))}
                            {selectedSendRecipientCount > 5 && (
                                <span className="inline-flex h-[28px] items-center rounded-full bg-[#F2F4F6] px-[10px] text-[12px] font-bold text-[#64748B]">
                                    외 {selectedSendRecipientCount - 5}명
                                </span>
                            )}
                            {selectedSendRecipientCount === 0 && (
                                <span className="inline-flex h-[28px] items-center rounded-full bg-[#F2F4F6] px-[10px] text-[12px] font-bold text-[#94A3B8]">
                                    {sendTargetMode === 'direct' ? '받는 사람을 선택하세요' : '이 추천 대상에 해당하는 회원이 없습니다'}
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="text-[12px] font-bold text-[#64748B]">보낼 선물</label>
                        {serverProducts.length === 0 ? (
                            <p className="mt-[8px] rounded-[8px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-[12px] py-[24px] text-center text-[12px] text-[#94A3B8]">
                                {serverData.productsStatus.isLoading ? '상품 정보를 불러오는 중…'
                                    : serverData.productsStatus.isError ? '상품 정보를 불러오지 못했어요.'
                                    : '보낼 수 있는 상품이 없습니다.'}
                            </p>
                        ) : (
                            <div className="mt-[8px] grid grid-cols-2 gap-[8px]">
                                {quickPickProducts.slice(0, 3).map((product) => (
                                    <button
                                        key={product.productId}
                                        type="button"
                                        onClick={() => {
                                            setSelectedProductId(product.productId);
                                            setCustomProduct(null);
                                        }}
                                        className={`rounded-[8px] border p-[12px] text-left ${
                                            !customProduct && selectedProduct?.id === product.productId ? 'border-[#3D73DD] bg-[#ECF1FC]' : 'border-[#E2E8F0] bg-white'
                                        }`}
                                    >
                                        <p className="text-[12px] font-bold text-[#3D73DD]">{product.isFeatured ? '추천 상품' : product.category}</p>
                                        <p className="mt-[4px] text-[14px] font-bold text-[#1F2937]">{product.brand}</p>
                                        <p className="text-[12px] text-[#64748B]">{product.name}</p>
                                        <p className="mt-[8px] text-[14px] font-bold text-[#111827]">{point(product.price)}</p>
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setCatalogTarget('send')}
                                    className={`flex min-h-[118px] flex-col items-center justify-center rounded-[8px] border p-[12px] text-center ${
                                        customProduct ? 'border-[#3D73DD] bg-[#ECF1FC]' : 'border-dashed border-[#A6BFEF] bg-[#F8FBFF]'
                                    }`}
                                >
                                    {customProduct ? (
                                        <>
                                            <p className="text-[12px] font-bold text-[#3D73DD]">직접 선택</p>
                                            <p className="mt-[4px] text-[14px] font-bold text-[#1F2937]">{customProduct.brand}</p>
                                            <p className="text-[12px] text-[#64748B]">{customProduct.name}</p>
                                            <p className="mt-[8px] text-[14px] font-bold text-[#111827]">{point(customProduct.price)}</p>
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={22} color="#3D73DD" />
                                            <p className="mt-[8px] text-[13px] font-bold text-[#3D73DD]">직접 선택</p>
                                            <p className="mt-[3px] text-[11px] leading-[16px] text-[#64748B]">전체 상품 {serverProducts.length}개에서 고르기</p>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        <label className="mt-[18px] block text-[12px] font-bold text-[#64748B]">메시지</label>
                        <div className="mt-[8px] flex flex-wrap gap-[6px]">
                            {Object.keys(MESSAGE_TEMPLATES).map((template) => (
                                <button
                                    key={template}
                                    type="button"
                                    onClick={() => setMessage(MESSAGE_TEMPLATES[template])}
                                    className={`h-[30px] rounded-full border px-[10px] text-[12px] font-bold ${
                                        message === MESSAGE_TEMPLATES[template]
                                            ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]'
                                            : 'border-[#CBD5E1] text-[#475569]'
                                    }`}
                                >
                                    {template}
                                </button>
                            ))}
                        </div>
                        <textarea
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            className="mt-[8px] h-[88px] w-full resize-none rounded-[8px] border border-[#CBD5E1] p-[12px] text-[14px] outline-none focus:border-[#3D73DD]"
                        />
                    </div>
                </div>
            </Card>

            <SummaryPanel
                title="발송 요약"
                rows={[
                    ['수신자', `${selectedSendRecipientCount}명`],
                    ['상품', selectedProduct ? `${selectedProduct.brand} ${selectedProduct.name}` : '상품 정보를 불러오는 중…'],
                    ['발송 시점', '지금 보내기'],
                    ['결제 방식', selectedPayment === 'balance' ? '포인트 사용 (최대 7% 충전시 할인)' : '바로 결제 (3% 할인)'],
                    ['예상 금액', selectedPayment === 'balance'
                        ? point((selectedProduct?.price ?? 0) * selectedSendRecipientCount)
                        : money(Math.round((selectedProduct?.price ?? 0) * 0.97) * selectedSendRecipientCount)],
                ]}
                actionLabel={sendInsufficientPoints ? '포인트 충전하기' : `${selectedSendRecipientCount}명에게 보내기`}
                onAction={sendInsufficientPoints ? () => setIsChargeDrawerOpen(true) : openSendConfirm}
                disabled={selectedSendRecipientCount === 0 || (!sendInsufficientPoints && !selectedProduct)}
            >
                <div className="mb-[12px] grid grid-cols-2 gap-[8px]">
                    {([
                        ['balance', '포인트'],
                        ['card', '바로 결제'],
                    ] as const).map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setSelectedPayment(key)}
                            className={`h-[36px] rounded-[8px] border text-[12px] font-bold ${
                                selectedPayment === key ? 'border-[#3D73DD] bg-[#ECF1FC] text-[#3D73DD]' : 'border-[#E2E8F0] text-[#64748B]'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {sendInsufficientPoints && (
                    <p className="mb-[10px] rounded-[8px] bg-[#FEF2F2] px-[10px] py-[8px] text-[11px] font-bold leading-[16px] text-[#DC2626]">
                        포인트가 부족합니다. 아래 버튼으로 바로 충전할 수 있어요.
                    </p>
                )}
            </SummaryPanel>
        </div>
    );

    const renderAutomation = () => (
        <div className="space-y-[18px]">
            {/* 월 한도 80%/100% 도달 배지 (D5) — 100% 이후 발동분은 보류함으로 이동한다 */}
            {autoLimit > 0 && autoSpent >= autoLimit && (
                <div className="flex items-start gap-[9px] rounded-[8px] border border-[#FCA5A5] bg-[#FEF2F2] px-[16px] py-[13px]">
                    <AlertCircle size={18} color="#DC2626" className="mt-[1px] shrink-0" />
                    <p className="text-[13px] font-bold leading-[20px] text-[#B91C1C]">
                        이번 달 자동 발송 한도에 도달했어요 — 이후 발동 건은 보류함에 모입니다. 한도를 올리거나 보류함에서 수동 발송해 주세요.
                    </p>
                </div>
            )}
            {autoLimit > 0 && autoSpent < autoLimit && autoSpent >= autoLimit * 0.8 && (
                <div className="flex items-start gap-[9px] rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] px-[16px] py-[13px]">
                    <AlertCircle size={18} color="#B45309" className="mt-[1px] shrink-0" />
                    <p className="text-[13px] font-bold leading-[20px] text-[#92400E]">
                        이번 달 자동 발송 사용액이 한도의 {autoSpentRate}%에 도달했어요. 한도를 넘으면 자동 발송이 보류됩니다.
                    </p>
                </div>
            )}
            <Card className="p-[22px]">
                <div className="flex items-center gap-[8px]">
                    <ShieldCheck size={19} color="#3D73DD" />
                    <SectionTitle title="자동 발송 월 예산 한도" description="한도에 도달하면 자동 발송을 보류하고 센터 관리자에게 알려드립니다." />
                </div>
                <div className="mt-[18px] grid grid-cols-[1fr_360px] gap-[14px]">
                    <div className="rounded-[8px] bg-[#F8FAFC] p-[16px]">
                        <div className="flex items-end justify-between gap-[12px]">
                            <span className="text-[13px] font-bold text-[#475569]">이번 달 자동 발송 사용액</span>
                            <span className="text-[13px] text-[#64748B]"><b className="text-[20px] text-[#111827]">{point(autoSpent)}</b> / {point(autoLimit)}</span>
                        </div>
                        <ProgressBar value={autoSpentRate} tone={autoSpentRate >= 90 ? 'red' : autoSpentRate >= 70 ? 'orange' : 'blue'} />
                        <p className="mt-[7px] text-[12px] text-[#64748B]">
                            {autoLimit > 0 ? `현재 한도의 ${autoSpentRate}%를 사용했습니다.` : '월 한도가 설정되지 않았습니다. 오른쪽에서 한도를 입력해 주세요.'}
                        </p>
                    </div>
                    <label className="rounded-[8px] border border-[#E2E8F0] p-[16px]">
                        <span className="text-[13px] font-bold text-[#334155]">월 한도 변경</span>
                        <span className="mt-[9px] flex items-center gap-[8px]">
                            <input
                                type="text"
                                inputMode="numeric"
                                value={autoLimitBlankAt === autoLimit ? '' : formatAmountText(String(autoLimit))}
                                onChange={(event) => {
                                    // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                    const digits = amountCaret.changeAmount(event, autoLimitBlankAt === autoLimit ? '' : autoLimit);
                                    if (digits == null) return;
                                    // 원칙 2 — 빈칸은 0 으로 바꾸지 않고 null 로 알려 저장을 막는다.
                                    changeAutoLimit(digits === '' ? null : Number(digits));
                                }}
                                // 이 입력은 600ms 뒤 자동 저장된다. 붙여넣은 CSV 조각이 통과하면
                                // 사용자가 알아채기 전에 다른 금액이 서버로 넘어간다. 정규형만 받는다.
                                onPaste={amountCaret.pasteAmount}
                                onDrop={amountCaret.dropAmount}
                                onBlur={() => setAutoLimitBlankAt(null)}
                                className="h-[38px] min-w-0 flex-1 rounded-[8px] border border-[#CBD5E1] px-[11px] text-right text-[14px] font-bold outline-none focus:border-[#3D73DD]"
                            />
                            <span className="text-[13px] text-[#64748B]">P</span>
                        </span>
                        <span className="mt-[7px] block text-[11px] text-[#94A3B8]">입력을 멈추면 자동으로 저장됩니다.</span>
                    </label>
                </div>
            </Card>

            {upcomingHistories.length > 0 && (
                <Card className="border-[#C3D4F4] p-[22px]">
                    <div className="flex items-center gap-[8px]"><Clock3 size={19} color="#3D73DD" /><SectionTitle title={`다가오는 발송 ${upcomingHistories.length}건`} description="예약 시점에 잔액을 다시 확인하며, 부족하면 자동으로 보류됩니다." /></div>
                    <div className="mt-[13px] max-h-[240px] space-y-[8px] overflow-y-auto pr-[4px]">{upcomingHistories.map((history) => <div key={history.id} className="flex items-center justify-between rounded-[8px] border border-[#DDE7F8] bg-[#F8FAFF] px-[14px] py-[11px]"><div><p className="text-[13px] font-bold text-[#1F2937]">{history.receiver} <span className="font-normal text-[#64748B]">· {history.product}</span></p><p className="mt-[2px] text-[11px] text-[#3D73DD]">{history.date} 발송 예정 · {history.reason} · {point(history.amount)}</p></div><button type="button" onClick={() => {
                        if (!history.batchId) {
                            window.alert('이 예약은 서버에서 취소할 수 없어요. 발송 내역에서 상태를 확인해 주세요.');
                            return;
                        }
                        if (!window.confirm(`${history.receiver}님에게 예약된 발송을 취소할까요?`)) return;
                        // 낙관적 제거는 하지 않는다 — 서버 확정 후 무효화 재조회로 목록이 갱신된다
                        cancelGiftBatchMutation.mutate(history.batchId, {
                            onError: (error) => alertMutationError(error, '예약 취소에 실패했어요. 잠시 후 다시 시도해 주세요.'),
                        });
                    }} className="h-[32px] rounded-[8px] border border-[#FCA5A5] bg-white px-[10px] text-[12px] font-bold text-[#DC2626]">예약 취소</button></div>)}</div>
                </Card>
            )}

            {heldAutomation.length > 0 && (
                <Card className="border-[#FDE68A] p-[22px]">
                    <div className="flex items-center gap-[8px]">
                        <AlertCircle size={19} color="#B45309" />
                        <SectionTitle title={`보류함 (발송 실패·대기) ${heldAutomation.length}건`} description="잔액 부족이나 발급 실패로 전달되지 못한 선물입니다. 수동 발송 실패 건도 여기에 모여요." />
                    </div>
                    <div className="mt-[14px] space-y-[8px]">
                        {heldAutomation.map((held) => (
                            <div key={held.id} className="flex items-center justify-between gap-[16px] rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] px-[14px] py-[12px]">
                                <div className="min-w-0">
                                    <p className="text-[14px] font-bold text-[#1F2937]">{held.receiver} <span className="font-normal text-[#64748B]">· {held.product} · {point(held.amount)}</span></p>
                                    <p className="mt-[2px] text-[12px] text-[#92400E]">{held.rule} · {held.reason}</p>
                                </div>
                                <div className="flex shrink-0 gap-[7px]">
                                    {/* 로컬 제거는 서버 성공 후에만 — 실패 시 목록이 조용히 사라지면 안 된다 */}
                                    <button type="button" disabled={resendHeldItemMutation.isLoading} onClick={() => {
                                        resendHeldItemMutation.mutate(held.id, {
                                            onSuccess: () => setHeldAutomation((prev) => prev.filter((item) => item.id !== held.id)),
                                            onError: (error) => alertMutationError(error, '보류된 선물을 다시 발송하지 못했어요.'),
                                        });
                                    }} className="h-[34px] rounded-[8px] bg-[#B45309] px-[12px] text-[12px] font-bold text-white disabled:opacity-50">지금 발송</button>
                                    <button type="button" onClick={() => {
                                        if (!window.confirm(`${held.receiver}님의 보류된 선물을 삭제할까요?`)) return;
                                        deleteHeldItemMutation.mutate(held.id, {
                                            onSuccess: () => setHeldAutomation((prev) => prev.filter((item) => item.id !== held.id)),
                                            onError: (error) => alertMutationError(error, '보류된 선물을 삭제하지 못했어요.'),
                                        });
                                    }} className="h-[34px] rounded-[8px] border border-[#D6B76C] bg-white px-[12px] text-[12px] font-bold text-[#92400E]">취소</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <Card className="p-[22px]">
                <div className="flex items-start justify-between gap-[16px]">
                    <SectionTitle title="자동 선물 규칙" description="조건이 맞으면 센터 이름으로 자동 발송됩니다. 처음에는 생일과 재등록 감사부터 운영해 보세요." />
                    <button
                        type="button"
                        onClick={() => {
                            setEditingRuleId(null);
                            setEditorTriggerType('birthday');
                            setIsRuleEditorOpen((prev) => !prev);
                        }}
                        className="inline-flex h-[38px] items-center gap-[7px] rounded-[8px] bg-[#3D73DD] px-[12px] text-[13px] font-bold text-white"
                    >
                        <Plus size={16} /> 새 규칙 만들기
                    </button>
                </div>

                <div className={`mt-[16px] flex items-center justify-between gap-[12px] rounded-[8px] px-[14px] py-[12px] ${automationEstimateCost > autoLimit ? 'bg-[#FEF2F2]' : 'bg-[#ECF1FC]'}`}>
                    <p className="text-[13px] text-[#475569]">켜진 규칙 <b>{activeAutomationRules.length}개</b> · 이번 달 예상 <b>{automationEstimateCount}건</b></p>
                    <p className={`text-[14px] font-bold ${automationEstimateCost > autoLimit ? 'text-[#DC2626]' : 'text-[#3D73DD]'}`}>예상 비용 {point(automationEstimateCost)}</p>
                </div>

                {isRuleEditorOpen && (
                    <form
                        key={editingRuleId ?? 'new'}
                        className="mt-[12px] rounded-[8px] border border-[#A6BFEF] bg-[#F8FAFF] p-[16px]"
                        onSubmit={(event) => {
                            event.preventDefault();
                            const form = new FormData(event.currentTarget);
                            const ruleMessage = String(form.get('message') ?? '').trim();
                            const triggerOption = findTriggerOption(editorTriggerType) ?? AUTOMATION_TRIGGER_OPTIONS[0];
                            const productId = String(form.get('product') || '');
                            const serverProduct = serverData.products?.find((item) => item.productId === productId);
                            if (!serverProduct) {
                                window.alert('선물 상품을 선택해 주세요.');
                                return;
                            }
                            const draft = {
                                name: String(form.get('name') || '새 자동 선물 규칙'),
                                condition: triggerOption.label,
                                timing: triggerOption.timingLabel,
                                product: `${serverProduct.brand} ${serverProduct.name}`,
                                price: serverProduct.price,
                                triggerType: triggerOption.value,
                                timingValue: triggerOption.defaultTiming,
                                productId: serverProduct.productId,
                                message: ruleMessage || undefined,
                            };
                            const request = {
                                name: draft.name,
                                trigger: { type: triggerOption.value },
                                timing: triggerOption.defaultTiming,
                                productId: serverProduct.productId,
                                message: ruleMessage || undefined,
                            };
                            if (editingRule) {
                                const previousRules = automationRules;
                                setAutomationRules((prev) => prev.map((item) => item.id === editingRule.id ? { ...item, ...draft, target: '회원' as const } : item));
                                updateAutomationRuleMutation.mutate({ ruleId: editingRule.id, request }, {
                                    onError: (error) => {
                                        setAutomationRules(previousRules);
                                        alertMutationError(error, '규칙을 저장하지 못했어요.');
                                    },
                                });
                            } else {
                                // 새 규칙은 서버가 발급한 ruleId로만 목록에 넣는다 (로컬 임시 id는 이후 수정·삭제가 불가)
                                createAutomationRuleMutation.mutate({ ...request, enabled: true }, {
                                    onError: (error) => alertMutationError(error, '규칙을 만들지 못했어요.'),
                                });
                            }
                            setEditingRuleId(null);
                            setIsRuleEditorOpen(false);
                        }}
                    >
                        <p className="text-[14px] font-bold text-[#1F2937]">{editingRule ? `규칙 편집 — ${editingRule.name}` : '새 자동 선물 규칙'}</p>
                        <div className="mt-[12px] grid grid-cols-4 gap-[10px]">
                            <label className="col-span-2 text-[12px] font-bold text-[#64748B]">규칙 이름<input name="name" required defaultValue={editingRule?.name} placeholder="예) 생일 축하 자동 발송" className="mt-[6px] h-[38px] w-full rounded-[8px] border border-[#CBD5E1] bg-white px-[10px] text-[13px] text-[#1F2937] outline-none" /></label>
                            <label className="text-[12px] font-bold text-[#64748B]">조건
                                <select
                                    name="condition"
                                    value={editorTriggerType}
                                    onChange={(event) => setEditorTriggerType(event.target.value as AutomationTriggerType)}
                                    className="mt-[6px] h-[38px] w-full rounded-[8px] border border-[#CBD5E1] bg-white px-[9px] text-[13px] text-[#1F2937]"
                                >
                                    {AUTOMATION_TRIGGER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {/* 기준·발송 주기(UX 기획 §4-4) — 서버 criteria/recurrence 우선, 구서버는 하드코딩 desc 폴백 */}
                                <span className="mt-[5px] block text-[11px] font-normal leading-[16px] text-[#94A3B8]">
                                    {segmentList.find((segment) => toApiSegmentKey(segment.key) === editorTriggerType)?.criteria
                                        || findTriggerOption(editorTriggerType)?.desc}
                                </span>
                                {!!segmentList.find((segment) => toApiSegmentKey(segment.key) === editorTriggerType)?.recurrence && (
                                    <span className="mt-[3px] block text-[11px] font-normal leading-[16px] text-[#94A3B8]">
                                        발송 주기: {segmentList.find((segment) => toApiSegmentKey(segment.key) === editorTriggerType)?.recurrence}
                                    </span>
                                )}
                            </label>
                            <div className="text-[12px] font-bold text-[#64748B]">발송 시점
                                <p className="mt-[6px] flex h-[38px] items-center rounded-[8px] border border-[#E2E8F0] bg-[#F1F5F9] px-[10px] text-[13px] font-bold text-[#475569]">
                                    {findTriggerOption(editorTriggerType)?.timingLabel ?? '조건 충족 시'}
                                </p>
                                <span className="mt-[5px] block text-[11px] font-normal text-[#94A3B8]">조건에 맞춰 자동으로 정해집니다.</span>
                            </div>
                            <label className="col-span-2 text-[12px] font-bold text-[#64748B]">선물 상품
                                <select
                                    name="product"
                                    required
                                    defaultValue={editingRule?.productId ?? ''}
                                    className="mt-[6px] h-[38px] w-full rounded-[8px] border border-[#CBD5E1] bg-white px-[9px] text-[13px] text-[#1F2937]"
                                >
                                    <option value="" disabled>{serverData.products ? '상품을 선택해 주세요' : '상품 목록을 불러오는 중…'}</option>
                                    {(serverData.products ?? []).map((product) => (
                                        <option key={product.productId} value={product.productId}>{product.brand} {product.name} · {point(product.price)}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="col-span-2 text-[12px] font-bold text-[#64748B]">메시지<input name="message" defaultValue={editingRule?.message ?? ''} placeholder="{이름} 회원님, 늘 감사합니다." className="mt-[6px] h-[38px] w-full rounded-[8px] border border-[#CBD5E1] bg-white px-[10px] text-[13px] text-[#1F2937] outline-none" /><span className="mt-[5px] block text-[11px] font-normal text-[#94A3B8]">{'{이름}'}은 발송 시 회원 이름으로 바뀝니다. 비워 두면 기본 문구로 발송됩니다.</span></label>
                        </div>
                        <div className="mt-[14px] flex justify-end gap-[8px]"><button type="button" onClick={() => { setEditingRuleId(null); setIsRuleEditorOpen(false); }} className="h-[36px] rounded-[8px] border border-[#CBD5E1] bg-white px-[13px] text-[12px] font-bold text-[#475569]">취소</button><button type="submit" className="h-[36px] rounded-[8px] bg-[#3D73DD] px-[14px] text-[12px] font-bold text-white">{editingRule ? '변경 사항 저장' : '규칙 저장'}</button></div>
                    </form>
                )}

                <div className="mt-[14px] space-y-[9px]">
                    {automationRules.map((rule) => (
                        <React.Fragment key={rule.id}>
                        <div className={`flex items-center justify-between gap-[16px] rounded-[8px] border px-[14px] py-[13px] ${rule.enabled ? 'border-[#C3D4F4] bg-[#FBFDFF]' : 'border-[#E2E8F0] bg-[#FAFAFA]'}`}>
                            <div className="flex min-w-0 items-center gap-[12px]">
                                <button type="button" aria-label={`${rule.name} ${rule.enabled ? '끄기' : '켜기'}`} onClick={() => {
                                    const previousRules = automationRules;
                                    setAutomationRules((prev) => prev.map((item) => item.id === rule.id ? { ...item, enabled: !item.enabled } : item));
                                    updateAutomationRuleMutation.mutate({ ruleId: rule.id, request: { enabled: !rule.enabled } }, {
                                        onError: (error) => {
                                            setAutomationRules(previousRules);
                                            alertMutationError(error, '규칙 켜기/끄기를 저장하지 못했어요.');
                                        },
                                    });
                                }}>
                                    {rule.enabled ? <ToggleRight size={36} color="#3D73DD" /> : <ToggleLeft size={36} color="#94A3B8" />}
                                </button>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-[7px]"><p className="truncate text-[14px] font-bold text-[#1F2937]">{rule.name}</p><Badge tone={rule.target === '회원' ? 'blue' : 'green'}>{rule.target} 대상</Badge></div>
                                    <p className="mt-[3px] text-[12px] text-[#64748B]">{rule.condition} · {rule.timing} · 월 약 {rule.estimate}건</p>
                                    <p className="mt-[3px] text-[11px] text-[#3D73DD]">최근 실행 {rule.recent}</p>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-[12px]">
                                <div className="text-right"><p className="max-w-[180px] truncate text-[12px] font-bold text-[#334155]">{rule.product}</p><p className="text-[12px] font-bold text-[#3D73DD]">{point(rule.price)}</p></div>
                                <button type="button" onClick={() => setHistoryRuleId((prev) => (prev === rule.id ? null : rule.id))} className={`h-[32px] rounded-[8px] border px-[10px] text-[12px] font-bold ${historyRuleId === rule.id ? 'border-[#3D73DD] text-[#3D73DD]' : 'border-[#CBD5E1] text-[#475569]'}`}>이력</button>
                                <button type="button" onClick={() => {
                                    setEditingRuleId(rule.id);
                                    // 기존 값 로드(F-16) — 구 enum 규칙은 재설정을 위해 기본 조건으로 연다
                                    setEditorTriggerType((findTriggerOption(rule.triggerType)?.value ?? 'birthday'));
                                    setIsRuleEditorOpen(true);
                                }} className="h-[32px] rounded-[8px] border border-[#CBD5E1] px-[10px] text-[12px] font-bold text-[#475569]">편집</button>
                                <button type="button" onClick={() => {
                                    if (!window.confirm(`'${rule.name}' 규칙을 삭제할까요?`)) return;
                                    const previousRules = automationRules;
                                    setAutomationRules((prev) => prev.filter((item) => item.id !== rule.id));
                                    deleteAutomationRuleMutation.mutate(rule.id, {
                                        onError: (error) => {
                                            setAutomationRules(previousRules);
                                            alertMutationError(error, '규칙을 삭제하지 못했어요.');
                                        },
                                    });
                                }} className="text-[12px] font-bold text-[#94A3B8]">삭제</button>
                            </div>
                        </div>
                        {historyRuleId === rule.id && (
                            <RuleExecutionHistory ruleId={rule.id} accessToken={serverData.accessToken} />
                        )}
                        </React.Fragment>
                    ))}
                    {automationRules.length === 0 && (
                        <p className="rounded-[8px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-[14px] py-[26px] text-center text-[13px] text-[#64748B]">
                            {serverData.isLoading ? '규칙을 불러오는 중입니다…' : '아직 만든 자동 선물 규칙이 없습니다.'}
                        </p>
                    )}
                </div>
            </Card>
        </div>
    );

    const renderHistory = () => (
        <div className="space-y-[18px]">
            {failedHistoryCount > 0 && (
                <button
                    type="button"
                    onClick={() => setSelectedTab('automation')}
                    className="flex w-full items-start gap-[9px] rounded-[8px] border border-[#FCA5A5] bg-[#FEF2F2] px-[16px] py-[13px] text-left hover:bg-[#FEE2E2]"
                >
                    <AlertCircle size={18} color="#DC2626" className="mt-[1px] shrink-0" />
                    <p className="text-[13px] font-bold leading-[20px] text-[#B91C1C]">발급 실패 {failedHistoryCount}건이 있어요 — 자동화 탭 보류함에서 다시 보낼 수 있어요</p>
                </button>
            )}
            <Card className="p-[22px]">
                <div className="flex items-start justify-between gap-[16px]">
                    <SectionTitle title="발송 내역" description="누가, 왜, 어떤 경로로 보냈는지 모든 발송 기록을 확인합니다." />
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const blob = await exportGiftHistoryAPI(serverData.accessToken, buildHistoryQuery({
                                    period: historyPeriod,
                                    startDate: historyStartDate,
                                    endDate: historyEndDate,
                                    receiverType: historyType,
                                    route: historyRoute,
                                    trainerName: historyTrainer,
                                    keyword: historyKeyword,
                                }));
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = '기프티콘_발송내역.csv';
                                // Safari·Firefox는 문서에 붙지 않은 링크의 click()·즉시 revoke를 무시한다
                                document.body.appendChild(link);
                                link.click();
                                setTimeout(() => {
                                    link.remove();
                                    URL.revokeObjectURL(url);
                                }, 0);
                            } catch {
                                window.alert('CSV 내보내기에 실패했습니다. 잠시 후 다시 시도해 주세요.');
                            }
                        }}
                        className="inline-flex h-[38px] items-center gap-[7px] rounded-[8px] border border-[#CBD5E1] bg-white px-[12px] text-[13px] font-bold text-[#334155]"
                    ><Download size={16} /> CSV 내보내기</button>
                </div>
                <div className="mt-[15px] flex flex-wrap items-center gap-[10px] rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] p-[10px]">
                    <div className="flex rounded-[7px] border border-[#E2E8F0] bg-white p-[3px]">
                        {([['all', '전체'], ['today', '오늘'], ['week', '최근 7일'], ['month', '이번 달'], ['custom', '직접 입력']] as const).map(([key, label]) => <button key={key} type="button" onClick={() => { setHistoryPeriod(key); setHistoryPage(1); }} className={`h-[30px] rounded-[6px] px-[11px] text-[12px] font-bold ${historyPeriod === key ? 'bg-[#ECF1FC] text-[#3D73DD]' : 'text-[#64748B] hover:bg-[#F8FAFC]'}`}>{label}</button>)}
                    </div>
                    {historyPeriod === 'custom' && (
                        <div className="flex items-center gap-[6px]">
                            <input type="date" value={historyStartDate} onChange={(event) => { setHistoryStartDate(event.target.value); setHistoryPage(1); }} className="h-[38px] rounded-[7px] border border-[#D8DEE8] bg-white px-[9px] text-[12px] font-bold text-[#475569] outline-none focus:border-[#3D73DD]" />
                            <span className="text-[12px] text-[#94A3B8]">~</span>
                            <input type="date" value={historyEndDate} onChange={(event) => { setHistoryEndDate(event.target.value); setHistoryPage(1); }} className="h-[38px] rounded-[7px] border border-[#D8DEE8] bg-white px-[9px] text-[12px] font-bold text-[#475569] outline-none focus:border-[#3D73DD]" />
                        </div>
                    )}
                    <label className="flex h-[38px] items-center gap-[8px] rounded-[7px] border border-[#D8DEE8] bg-white px-[10px]"><span className="whitespace-nowrap text-[11px] font-bold text-[#94A3B8]">받는 대상</span><select value={historyType} onChange={(event) => { setHistoryType(event.target.value); setHistoryPage(1); }} className="bg-transparent text-[12px] font-bold text-[#475569] outline-none"><option value="전체">전체</option>{['회원', '강사'].map((item) => <option key={item}>{item}</option>)}</select></label>
                    <label className="flex h-[38px] items-center gap-[8px] rounded-[7px] border border-[#D8DEE8] bg-white px-[10px]"><span className="whitespace-nowrap text-[11px] font-bold text-[#94A3B8]">담당 강사</span><select value={historyTrainer} onChange={(event) => { setHistoryTrainer(event.target.value); setHistoryPage(1); }} className="bg-transparent text-[12px] font-bold text-[#475569] outline-none"><option value="전체">전체</option>{historyTrainerOptions.map((name) => <option key={name}>{name}</option>)}</select></label>
                    <label className="flex h-[38px] items-center gap-[8px] rounded-[7px] border border-[#D8DEE8] bg-white px-[10px]"><span className="whitespace-nowrap text-[11px] font-bold text-[#94A3B8]">발송 경로</span><select value={historyRoute} onChange={(event) => { setHistoryRoute(event.target.value); setHistoryPage(1); }} className="max-w-[130px] bg-transparent text-[12px] font-bold text-[#475569] outline-none">{['전체', '직접 발송', '대시보드 추천', '자동 규칙', '단체 캠페인', '강사 발송'].map((item) => <option key={item}>{item}</option>)}</select></label>
                    <label className="ml-auto flex h-[38px] w-[220px] items-center gap-[8px] rounded-[7px] border border-[#D8DEE8] bg-white px-[11px] focus-within:border-[#3D73DD]"><Search size={15} color="#94A3B8" /><input value={historyKeyword} onChange={(event) => { setHistoryKeyword(event.target.value); setHistoryPage(1); }} placeholder="받는 사람 검색" className="min-w-0 flex-1 bg-transparent text-[12px] outline-none" /></label>
                </div>
                {/* 통계는 서버 집계(전체 기간) 기준 — 아래 표의 화면 필터와는 별개다 */}
                <div className="mt-[12px] grid grid-cols-5 divide-x divide-[#E2E8F0] rounded-[8px] border border-[#E2E8F0] bg-white py-[13px]">
                    {[
                        ['발송 건수', historySummary ? `${historySummary.count}건` : '-'],
                        ['총 발송액', historySummary ? point(historySummary.totalAmount) : '-'],
                        ['선물 받은 회원', historySummary ? `${historySummary.giftedMemberCount}명` : '-'],
                        ['발송 후 재등록', historySummary ? `${historySummary.reregCount}명` : '-'],
                        ['재등록 전환율', historySummary ? `${historySummary.reregRate}%` : '-'],
                    ].map(([label, value]) => <div key={label} className="px-[16px]"><p className="text-[11px] font-bold text-[#94A3B8]">{label}</p><p className="mt-[3px] text-[17px] font-bold text-[#111827]">{value}</p></div>)}
                </div>
                <p className="mt-[7px] text-[11px] text-[#94A3B8]">위 통계는 전체 기간 서버 집계입니다.{isHistoryTruncated ? ' 아래 표는 최근 100건만 표시하며, 전체 내역은 CSV 내보내기로 확인해 주세요.' : ''}</p>
                <div className="mt-[12px] overflow-x-auto rounded-[8px] border border-[#E2E8F0]">
                    <div className="min-w-[1150px]">
                        <div className="grid grid-cols-[96px_105px_minmax(160px,1.1fr)_minmax(180px,1.25fr)_112px_minmax(150px,1fr)_104px_100px] items-center bg-[#F8FAFC] px-[16px] py-[11px] text-[11px] font-bold text-[#64748B]"><span>상태</span><span>일시</span><span>받는 사람</span><span>사유 / 결제</span><span>발송 경로</span><span>상품</span><span>재등록</span><span className="text-right">금액</span></div>
                        {filteredHistories.length === 0 && (
                            <p className="py-[34px] text-center text-[13px] text-[#94A3B8]">
                                {serverData.historyStatus.isLoading ? '발송 내역을 불러오는 중입니다…'
                                    : serverData.historyStatus.isError ? '발송 내역을 불러오지 못했어요.'
                                    : '조건에 맞는 발송 내역이 없습니다.'}
                            </p>
                        )}
                        {pagedHistories.map((history) => (
                            <div key={history.id} onClick={() => setHistoryDetail(history)} className="grid min-h-[70px] cursor-pointer grid-cols-[96px_105px_minmax(160px,1.1fr)_minmax(180px,1.25fr)_112px_minmax(150px,1fr)_104px_100px] items-center border-t border-[#E8ECF2] px-[16px] py-[11px] text-[12px] text-[#334155] hover:bg-[#FBFCFE]">
                                <span className="justify-self-start">
                                    <Badge tone={getHistoryStatusTone(history.displayStatus, history.status)}>{history.status}</Badge>
                                </span>
                                <span className="whitespace-nowrap font-medium text-[#475569]">{history.date}</span>
                                <span className="min-w-0 pr-[14px]"><b className="block truncate text-[13px] text-[#1F2937]">{history.receiver}</b><span className="mt-[2px] block truncate text-[11px] text-[#94A3B8]">{history.receiverType}{history.trainerName ? ` · 담당 ${history.trainerName}` : ''}</span></span>
                                <span className="min-w-0 pr-[16px]"><span className="block truncate text-[13px] text-[#334155]">{history.reason}</span><span className="mt-[2px] block text-[11px] text-[#94A3B8]">{history.payment}</span></span>
                                <span className="justify-self-start rounded-[6px] bg-[#F2F4F6] px-[8px] py-[5px] text-[11px] font-bold text-[#64748B]">{history.route}</span>
                                <span className="min-w-0 truncate pr-[14px] text-[13px] text-[#334155]" title={history.product}>{history.product}</span>
                                <span className="justify-self-start">
                                    {history.reregCount !== undefined ? <Badge tone="green">재등록 {history.reregCount}명</Badge>
                                        : history.reregStatus === 'YES' ? <Badge tone="green">재등록 ✓</Badge>
                                        : history.reregStatus === 'EXPIRED' ? <Badge tone="gray">만료</Badge>
                                        : history.reregStatus === 'ACTIVE' ? <Badge tone="orange">활성</Badge>
                                        : <span className="text-[12px] text-[#CBD5E1]">-</span>}
                                </span>
                                <b className="whitespace-nowrap text-right text-[13px] tabular-nums text-[#1F2937]">{history.payment === '바로 결제' ? money(history.amount) : point(history.amount)}</b>
                            </div>
                        ))}
                    </div>
                </div>
                {totalHistoryPages > 1 && (
                    <div className="mt-[14px] flex items-center justify-center gap-[6px]">
                        <button
                            type="button"
                            disabled={currentHistoryPage === 1}
                            onClick={() => setHistoryPage(currentHistoryPage - 1)}
                            className="h-[32px] rounded-[7px] border border-[#CBD5E1] bg-white px-[10px] text-[12px] font-bold text-[#475569] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            이전
                        </button>
                        {Array.from({ length: totalHistoryPages }, (_, index) => index + 1).map((page) => (
                            <button
                                key={page}
                                type="button"
                                onClick={() => setHistoryPage(page)}
                                className={`h-[32px] w-[32px] rounded-[7px] text-[12px] font-bold ${
                                    currentHistoryPage === page ? 'bg-[#3D73DD] text-white' : 'border border-[#E2E8F0] bg-white text-[#64748B] hover:bg-[#F8FAFC]'
                                }`}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            type="button"
                            disabled={currentHistoryPage === totalHistoryPages}
                            onClick={() => setHistoryPage(currentHistoryPage + 1)}
                            className="h-[32px] rounded-[7px] border border-[#CBD5E1] bg-white px-[10px] text-[12px] font-bold text-[#475569] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            다음
                        </button>
                    </div>
                )}
            </Card>
        </div>
    );

    const renderContent = () => {
        if (selectedTab === 'home') return renderHome();
        if (selectedTab === 'send') return renderSend();
        if (selectedTab === 'automation') return renderAutomation();
        if (selectedTab === 'history') return renderHistory();
        return renderHome();
    };

    return (
        <div className="min-h-screen w-full bg-[#F4F5F7] pt-[88px]">
            <div className="mx-auto w-full max-w-[1440px] px-[40px] py-[34px]">
                <div className="flex items-start justify-between gap-[28px]">
                    <div>
                        <div className="flex items-center gap-[10px]">
                            <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[8px] bg-[#ECF1FC] text-[#3D73DD]">
                                <Gift size={20} />
                            </div>
                            <h1 className="text-[26px] font-bold leading-[34px] text-[#111827]">기프티콘 선물 관리</h1>
                            <Badge tone="green">NEW</Badge>
                        </div>
                        <p className="mt-[6px] text-[14px] leading-[22px] text-[#64748B]">
                            센터가 챙길 선물 타이밍을 직접 고르고, 발송·캠페인·자동화·강사 보상을 한 흐름에서 운영합니다.
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-[10px]">
                        <div className="flex w-[330px] items-center justify-between gap-[12px] rounded-[8px] border border-[#E2E8F0] bg-white px-[16px] py-[11px] shadow-[0_6px_18px_rgba(15,23,42,0.04)]">
                            <div className="flex min-w-0 items-center gap-[10px]">
                                <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[8px] bg-[#ECF1FC]"><UserCheck size={19} color="#3D73DD" /></div>
                                <div className="min-w-0">
                                    <p className="whitespace-nowrap text-[12px] font-bold text-[#3D73DD]">강사별 센터 예산 사용 권한</p>
                                    <p className="mt-[2px] text-[11px] font-bold text-[#64748B]">권한 보유 {enabledTrainerCount} / {trainers.length}명</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsTrainerPermissionOpen(true)}
                                className="inline-flex h-[40px] shrink-0 items-center gap-[6px] rounded-[8px] bg-[#3D73DD] px-[13px] text-[12px] font-bold text-white"
                            >
                                <Settings2 size={15} /> 설정
                            </button>
                        </div>
                        <div className="flex w-[360px] items-center justify-between gap-[14px] rounded-[8px] border border-[#3D73DD] bg-white px-[16px] py-[11px] shadow-[0_6px_18px_rgba(61,115,221,0.1)]">
                            <div className="flex min-w-0 items-center gap-[10px]">
                                <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[8px] bg-[#3D73DD]"><Coins size={19} color="white" /></div>
                                <div className="min-w-0">
                                    <p className="text-[12px] font-bold text-[#3D73DD]">남은 예산</p>
                                    <p className="mt-[1px] text-[20px] font-bold text-[#111827]">{point(pointBalance)}</p>
                                </div>
                            </div>
                            <button type="button" onClick={() => setIsChargeDrawerOpen(true)} className="inline-flex h-[40px] shrink-0 items-center gap-[6px] rounded-[8px] bg-[#3D73DD] px-[13px] text-[12px] font-bold text-white"><Coins size={15} /> 포인트 충전</button>
                        </div>
                    </div>
                </div>

                <div className="mt-[24px] flex flex-wrap gap-[8px] rounded-[8px] border border-[#E2E8F0] bg-white p-[6px]">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = selectedTab === tab.key;

                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setSelectedTab(tab.key)}
                                className={`inline-flex h-[40px] items-center gap-[7px] rounded-[7px] px-[13px] text-[13px] font-bold ${
                                    active ? 'bg-[#3D73DD] text-white' : 'text-[#64748B] hover:bg-[#F8FAFC]'
                                }`}
                            >
                                <Icon size={16} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {serverData.isError && (
                    <div className="mt-[18px] flex items-start justify-between gap-[16px] rounded-[8px] border border-[#FCA5A5] bg-[#FEF2F2] px-[16px] py-[13px]">
                        <div className="flex items-start gap-[9px]">
                            <AlertCircle size={18} color="#DC2626" className="mt-[1px] shrink-0" />
                            <div>
                                <p className="text-[13px] font-bold leading-[20px] text-[#B91C1C]">데이터를 불러오지 못했어요</p>
                                <p className="mt-[2px] text-[12px] leading-[18px] text-[#DC2626]">일부 정보가 비어 보일 수 있습니다. 다시 시도해 주세요.</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={serverData.refetchAll}
                            className="h-[34px] shrink-0 rounded-[8px] border border-[#FCA5A5] bg-white px-[12px] text-[12px] font-bold text-[#DC2626] hover:bg-[#FEF2F2]"
                        >
                            다시 시도
                        </button>
                    </div>
                )}
                {serverData.isLoading && (
                    <p className="mt-[18px] rounded-[8px] border border-[#E2E8F0] bg-white px-[16px] py-[13px] text-[13px] font-bold text-[#64748B]">
                        선물 데이터를 불러오는 중입니다…
                    </p>
                )}

                <div className="mt-[18px]">{renderContent()}</div>
            </div>

            {isSegmentSettingOpen && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(15,23,42,0.38)] px-[24px]">
                    <div className="w-full max-w-[620px] rounded-[8px] bg-white p-[24px] shadow-[0_24px_60px_rgba(15,23,42,0.24)]">
                        <div className="flex items-start justify-between gap-[18px]">
                            <SectionTitle
                                title="홈 선물 타이밍 설정"
                                description="기본으로 모든 상황이 표시됩니다. 필요 없는 상황은 꺼 주세요. 상황별 추천 선물도 여기서 바꿀 수 있어요."
                            />
                            <button
                                onClick={() => saveHomeSegments(segmentList.map((segment) => segment.key))}
                                className="h-[34px] flex-shrink-0 whitespace-nowrap rounded-[8px] border border-[#CBD5E1] px-[11px] text-[12px] font-bold text-[#475569] hover:bg-[#F8FAFC]"
                            >
                                기본값 (전체)
                            </button>
                        </div>

                        <div className="mt-[18px] grid grid-cols-2 gap-[10px]">
                            {segmentList.map((segment) => {
                                const checked = homeSegments.includes(segment.key);

                                return (
                                    <div
                                        key={segment.key}
                                        onClick={() => toggleHomeSegment(segment.key)}
                                        className={`cursor-pointer rounded-[8px] border p-[12px] ${
                                            checked ? 'border-[#3D73DD] bg-[#ECF1FC]' : 'border-[#E2E8F0] bg-white hover:bg-[#F8FAFC]'
                                        }`}
                                    >
                                        <div className="flex items-start gap-[10px]">
                                            <span className={`mt-[1px] flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[6px] border ${checked ? 'border-[#3D73DD] bg-[#3D73DD]' : 'border-[#CBD5E1]'}`}>
                                                {checked && <Check size={15} color="white" />}
                                                {!checked && <Plus size={14} color="#CBD5E1" />}
                                            </span>
                                            <span>
                                                <b className="text-[14px] text-[#1F2937]">{segment.title}</b>
                                                <p className="mt-[2px] text-[12px] leading-[18px] text-[#64748B]">{segment.description}</p>
                                            </span>
                                        </div>
                                        {checked && (
                                            <div
                                                onClick={(event) => event.stopPropagation()}
                                                className="mt-[10px] flex cursor-default items-center justify-between gap-[8px] rounded-[8px] border border-[#DDE7F8] bg-white px-[10px] py-[8px]"
                                            >
                                                <div className="min-w-0">
                                                    <p className="text-[10px] text-[#94A3B8]">추천 선물</p>
                                                    <p className="truncate text-[12px] font-bold text-[#334155]">
                                                        {segment.recommendedGift} · <span className="text-[#3D73DD]">{point(segment.unitPrice)}</span>
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setCatalogTarget({ segment: segment.key })}
                                                    className="h-[28px] flex-shrink-0 rounded-[7px] border border-[#A6BFEF] bg-white px-[9px] text-[11px] font-bold text-[#3D73DD]"
                                                >
                                                    변경
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-[20px] flex justify-end gap-[8px]">
                            <button
                                onClick={() => setIsSegmentSettingOpen(false)}
                                className="h-[40px] rounded-[8px] border border-[#CBD5E1] px-[16px] text-[13px] font-bold text-[#334155]"
                            >
                                닫기
                            </button>
                            <button
                                onClick={() => setIsSegmentSettingOpen(false)}
                                className="h-[40px] rounded-[8px] bg-[#3D73DD] px-[18px] text-[13px] font-bold text-white"
                            >
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isChargeDrawerOpen && (
                <ChargeDrawer
                    balance={pointBalance}
                    unconfirmedAmount={unconfirmedAmount}
                    chargePolicy={serverData.chargePolicy}
                    policyStatus={serverData.chargePolicyStatus}
                    products={serverData.products}
                    isSubmitting={createChargeMutation.isLoading}
                    onClose={() => setIsChargeDrawerOpen(false)}
                    onComplete={completeCharge}
                />
            )}
            {catalogTarget && (
                <ProductSelectModal
                    products={serverData.products}
                    isLoading={serverData.productsStatus.isLoading}
                    isError={serverData.productsStatus.isError}
                    onClose={() => setCatalogTarget(null)}
                    onSelect={(product) => {
                        if (catalogTarget === 'send') {
                            setCustomProduct(product);
                            setSelectedProductId('');
                        } else if (typeof catalogTarget === 'object' && 'confirmGroup' in catalogTarget) {
                            setSendPayload((current) => current ? {
                                ...current,
                                giftGroups: current.giftGroups.map((group) => (
                                    catalogTarget.confirmGroup === null || group.key === catalogTarget.confirmGroup
                                        ? { ...group, product: toSendProduct(product) }
                                        : group
                                )),
                            } : current);
                        } else if (typeof catalogTarget === 'object' && 'segment' in catalogTarget) {
                            const previousSegments = segmentList;
                            setSegmentList((prev) => prev.map((segment) => segment.key === catalogTarget.segment
                                ? {
                                    ...segment,
                                    recommendedGift: `${product.brand} ${product.name}`,
                                    unitPrice: product.price,
                                    recommendedProduct: toSendProduct(product),
                                }
                                : segment));
                            setRecommendedProductMutation.mutate({
                                segmentKey: toApiSegmentKey(catalogTarget.segment),
                                productId: product.productId,
                            }, {
                                onError: (error) => {
                                    setSegmentList(previousSegments);
                                    alertMutationError(error, '추천 선물을 저장하지 못했어요.');
                                },
                            });
                        }
                        setCatalogTarget(null);
                    }}
                />
            )}
            {isConfirmingPayment && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[rgba(15,23,42,0.55)] px-[24px]">
                    <div className="w-full max-w-[380px] rounded-[8px] bg-white p-[26px] text-center shadow-[0_24px_60px_rgba(15,23,42,0.3)]">
                        <div className="mx-auto h-[36px] w-[36px] animate-spin rounded-full border-[3px] border-[#DCE6F8] border-t-[#3D73DD]" />
                        <h3 className="mt-[14px] text-[16px] font-bold text-[#1F2937]">결제를 승인하고 선물을 발급하는 중입니다</h3>
                        <p className="mt-[6px] text-[12px] leading-[18px] text-[#64748B]">
                            수신자가 많으면 시간이 조금 걸릴 수 있어요.
                            <br />
                            창을 닫거나 새로고침하지 말아 주세요.
                        </p>
                    </div>
                </div>
            )}
            {sendPayload && (
                <SendConfirmModal
                    payload={sendPayload}
                    pointBalance={pointBalance}
                    onConfirm={confirmSend}
                    onRequestProductChange={(groupKey) => setCatalogTarget({ confirmGroup: groupKey ?? null })}
                    onRequestCharge={() => setIsChargeDrawerOpen(true)}
                    onClose={() => setSendPayload(null)}
                    onSent={() => {
                        // 발송 성공 — 선택을 비워 "한 번 더 클릭 = 이중 발송" 경로를 차단하고 결과 화면으로
                        setDirectSelectedMemberIds([]);
                        setHomeSelectedIds([]);
                        setSelectedCoachNames([]);
                        setSelectedTab('history');
                    }}
                />
            )}
            {historyDetail && <HistoryDetailModal history={historyDetail} accessToken={serverData.accessToken} onClose={() => setHistoryDetail(null)} />}
            {isTrainerPermissionOpen && (
                <TrainerPermissionModal
                    trainers={trainers}
                    isLoading={serverData.isLoading}
                    onToggle={(id) => {
                        const target = trainers.find((item) => item.id === id);
                        if (!target) return;
                        setTrainers((prev) => prev.map((item) => item.id === id ? { ...item, budgetEnabled: !item.budgetEnabled, monthlyLimit: item.monthlyLimit || 50000 } : item));
                        updateBudgetPermissionMutation.mutate({
                            trainerId: id,
                            request: { enabled: !target.budgetEnabled, monthlyLimit: target.monthlyLimit || 50000 },
                        }, {
                            onError: (error) => {
                                rollbackTrainer(id);
                                alertMutationError(error, '강사 예산 권한을 저장하지 못했어요.');
                            },
                        });
                    }}
                    onLimitChange={(id, limit) => {
                        // 디바운스 콜백은 클로저의 낡은 trainers 대신 ref로 저장 시점의 최신 enabled를 읽는다
                        if (limitSaveTimers.current[id]) clearTimeout(limitSaveTimers.current[id]);
                        if (limit === null) {
                            /*
                             * 입력이 비었다. `Number('') === 0` 으로 한도 0P 를 자동 저장하던 경로를 막는다.
                             * 저장하지 않으므로 로컬 값이 서버와 어긋난 채 남지 않게 월 한도만 서버 실값으로
                             * 되돌린다(권한 토글까지 되돌리면 방금 켠 권한이 도로 꺼져 보인다).
                             */
                            const serverTrainer = serverData.trainers?.find((item) => item.id === id);
                            if (serverTrainer) {
                                setTrainers((prev) => prev.map((item) => item.id === id
                                    ? { ...item, monthlyLimit: serverTrainer.monthlyLimit }
                                    : item));
                            }
                            return;
                        }
                        setTrainers((prev) => prev.map((item) => item.id === id ? { ...item, monthlyLimit: limit } : item));
                        limitSaveTimers.current[id] = setTimeout(() => {
                            const target = trainersRef.current.find((item) => item.id === id);
                            updateBudgetPermissionMutation.mutate({
                                trainerId: id,
                                request: { enabled: target?.budgetEnabled ?? true, monthlyLimit: limit },
                            }, {
                                onError: (error) => {
                                    rollbackTrainer(id);
                                    alertMutationError(error, '강사 월 한도를 저장하지 못했어요.');
                                },
                            });
                        }, 600);
                    }}
                    onShowUsage={(trainerName) => {
                        setIsTrainerPermissionOpen(false);
                        setHistoryTrainer(trainerName);
                        setHistoryRoute('강사 발송');
                        setHistoryType('전체');
                        setHistoryPeriod('all');
                        setHistoryKeyword('');
                        setHistoryPage(1);
                        setSelectedTab('history');
                    }}
                    onClose={() => setIsTrainerPermissionOpen(false)}
                />
            )}
        </div>
    );
};

// TODO(TEMP-CHARGE-CAP): ★임시★ 충전 보유 상한 (FE 전용, 2026-08-15) — 충전 고도화 때 제한 해제 예정.
//   해제 방법: "TEMP-CHARGE-CAP" 으로 검색해 이 상수와 모든 사용처(4곳)를 함께 제거하면 끝.
const TEMP_CHARGE_MAX_BALANCE = 1_000_000;

const ChargeDrawer = ({
    balance,
    unconfirmedAmount,
    chargePolicy,
    policyStatus,
    products,
    isSubmitting,
    onClose,
    onComplete,
}: {
    balance: number;
    unconfirmedAmount: number;
    chargePolicy: ChargePolicy | null;
    policyStatus: { isLoading: boolean; isError: boolean };
    products: GiftCatalogProduct[] | null;
    isSubmitting: boolean;
    onClose: () => void;
    onComplete: (charge: { depositorName: string; grantedAmount: number }) => Promise<boolean>;
}) => {
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [selectedPointAmount, setSelectedPointAmount] = useState<number | null>(null);
    const [depositorName, setDepositorName] = useState('');
    const [consent, setConsent] = useState(false);
    const [copied, setCopied] = useState(false);
    // 티어·계좌·약관 버전은 전부 서버 정책(GET /gifts/charges/policy) 값 — FE 하드코딩 금지
    const pointTiers = chargePolicy?.tiers ?? [];
    const account = chargePolicy?.account ?? null;
    // TODO(TEMP-CHARGE-CAP): ★임시★ 제한 해제 시 이 블록 제거 (isTierBlocked → 항상 false)
    const isTierBlocked = (tier: ChargeTier) => balance + tier.pointAmount > TEMP_CHARGE_MAX_BALANCE;
    const selectableTiers = pointTiers.filter((tier) => !isTierBlocked(tier));
    const hasBlockedTier = pointTiers.length > selectableTiers.length;
    const selectedTier = selectableTiers.find((tier) => tier.pointAmount === selectedPointAmount) ?? selectableTiers[0] ?? null;
    /** 할인액을 기준 상품(서버가 지정한 benefitProductId) 잔 수로 환산한 마케팅 문구 */
    const benefitLabel = (tier: ChargeTier) => {
        const source = products?.find((item) => item.productId === tier.benefitProductId);
        if (!source || tier.discountAmount <= 0 || source.price <= 0) return null;
        const cups = Math.floor(tier.discountAmount / source.price);
        if (cups < 1) return null;
        return `${source.brand} 약 ${cups}잔이 덤`;
    };
    const discountAmount = selectedTier?.discountAmount ?? 0;
    const refundExamplePoint = (selectedTier?.pointAmount ?? 0) / 2;
    const refundExampleAmount = (selectedTier?.price ?? 0) / 2;
    const stepLabels = ['포인트 선택', '입금 안내', '입금 확인', '완료'];

    const goNext = async () => {
        if (!selectedTier) return;
        if (step === 1) setStep(2);
        if (step === 2 && depositorName.trim()) setStep(3);
        if (step === 3 && consent) {
            // 서버가 신청을 받아들인 뒤에만 완료 화면으로 넘어간다
            const created = await onComplete({
                depositorName: depositorName.trim(),
                grantedAmount: selectedTier.pointAmount,
            });
            if (created) setStep(4);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex justify-end bg-[rgba(15,23,42,0.4)]" onClick={onClose}>
            <aside className="flex h-full w-full max-w-[500px] flex-col bg-white shadow-[-18px_0_48px_rgba(15,23,42,0.18)]" onClick={(event) => event.stopPropagation()}>
                <div className="flex items-start justify-between border-b border-[#E2E8F0] px-[24px] py-[20px]">
                    <div><div className="flex items-center gap-[8px]"><div className="flex h-[34px] w-[34px] items-center justify-center rounded-[8px] bg-[#ECF1FC]"><Coins size={18} color="#3D73DD" /></div><h2 className="text-[20px] font-bold text-[#111827]">선물 포인트 충전</h2></div><p className="mt-[6px] text-[12px] text-[#64748B]">무통장입금 완료 선언 후 포인트가 즉시 지급됩니다.</p></div>
                    <button type="button" aria-label="충전 닫기" onClick={onClose} className="flex h-[34px] w-[34px] items-center justify-center rounded-[8px] text-[#64748B] hover:bg-[#F2F4F6]"><X size={19} /></button>
                </div>

                <div className="border-b border-[#E2E8F0] px-[24px] py-[14px]">
                    <div className="grid grid-cols-4 gap-[6px]">
                        {stepLabels.map((label, index) => {
                            const stepNumber = index + 1;
                            const active = step === stepNumber;
                            const completed = step > stepNumber;
                            return <div key={label}><div className={`h-[4px] rounded-full ${active || completed ? 'bg-[#3D73DD]' : 'bg-[#E2E8F0]'}`} /><p className={`mt-[6px] text-[10px] font-bold ${active ? 'text-[#3D73DD]' : completed ? 'text-[#64748B]' : 'text-[#94A3B8]'}`}>{stepNumber}. {label}</p></div>;
                        })}
                    </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-[24px] py-[22px]">
                    {step === 1 && (
                        <div>
                            <SectionTitle title="구매할 포인트를 선택하세요" description="포인트가 클수록 할인율이 높아집니다." />
                            <div className="mt-[18px] grid grid-cols-2 gap-[10px]">
                                {pointTiers.map((tier) => {
                                    const blocked = isTierBlocked(tier);
                                    const selected = !blocked && selectedTier?.pointAmount === tier.pointAmount;
                                    const benefit = benefitLabel(tier);
                                    return (
                                        <button
                                            key={tier.pointAmount}
                                            type="button"
                                            disabled={blocked}
                                            onClick={() => setSelectedPointAmount(tier.pointAmount)}
                                            className={`min-h-[112px] rounded-[8px] border p-[14px] text-left ${
                                                blocked
                                                    ? 'cursor-not-allowed border-[#E2E8F0] bg-[#F8FAFC] opacity-60'
                                                    : selected
                                                        ? 'border-[#3D73DD] bg-[#F8FAFF]'
                                                        : 'border-[#E2E8F0] bg-white hover:border-[#A6BFEF]'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <b className="text-[18px] text-[#111827]">{point(tier.pointAmount)}</b>
                                                <span className={`flex h-[20px] w-[20px] items-center justify-center rounded-full border ${selected ? 'border-[#3D73DD] bg-[#3D73DD]' : 'border-[#CBD5E1]'}`}>{selected && <Check size={13} color="white" />}</span>
                                            </div>
                                            <div className="mt-[7px] flex flex-wrap items-baseline gap-[6px]">
                                                {tier.discountAmount > 0 && <span className="text-[11px] text-[#94A3B8] line-through">{money(tier.pointAmount)}</span>}
                                                <b className="text-[14px] text-[#3D73DD]">{money(tier.price)}</b>
                                                {tier.discountRate > 0 && <span className="rounded-full bg-[#E5F8F3] px-[6px] py-[2px] text-[10px] font-bold text-[#008C7B]">{tier.discountRate}% 할인</span>}
                                            </div>
                                            <p className={`mt-[7px] text-[11px] font-bold ${blocked ? 'text-[#C9353F]' : benefit ? 'text-[#00A793]' : 'text-[#94A3B8]'}`}>
                                                {blocked ? '보유 한도 초과 — 충전 불가' : benefit ?? '기본 구매'}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                            {/* TODO(TEMP-CHARGE-CAP): ★임시★ 제한 해제 시 이 배너 제거 */}
                            {hasBlockedTier && (
                                <p className="mt-[10px] rounded-[8px] border border-[#FDE68A] bg-[#FFFBEB] px-[12px] py-[8px] text-[12px] leading-[17px] text-[#92400E]">
                                    센터 포인트는 현재 최대 {point(TEMP_CHARGE_MAX_BALANCE)}까지 보유할 수 있어요 (지금 잔액 {point(balance)}).
                                    한도는 곧 확대될 예정이에요.
                                </p>
                            )}
                            {!selectedTier && (
                                <p className="mt-[18px] rounded-[8px] border border-dashed border-[#CBD5E1] bg-[#F8FAFC] px-[14px] py-[22px] text-center text-[13px] text-[#64748B]">
                                    {/* TODO(TEMP-CHARGE-CAP): ★임시★ 제한 해제 시 아래 한도 도달 분기 제거 */}
                                    {policyStatus.isLoading
                                        ? '충전 정책을 불러오는 중입니다…'
                                        : pointTiers.length > 0
                                            ? `보유 한도(${point(TEMP_CHARGE_MAX_BALANCE)})에 도달해 지금은 충전할 수 없어요. 포인트를 사용한 뒤 다시 충전해 주세요.`
                                            : '충전 정책을 불러오지 못해 지금은 충전할 수 없어요. 잠시 후 다시 시도해 주세요.'}
                                </p>
                            )}
                            {selectedTier && (
                                <div className="mt-[16px] rounded-[8px] bg-[#F8FAFC] p-[14px]"><SummaryRow label="구매 포인트" value={point(selectedTier.pointAmount)} /><SummaryRow label="할인" value={discountAmount > 0 ? `-${money(discountAmount)}` : '없음'} accent /><div className="mt-[10px] border-t border-[#E2E8F0] pt-[10px]"><SummaryRow label="결제(입금) 금액" value={money(selectedTier.price)} strong /></div></div>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <SectionTitle title="아래 계좌로 입금해 주세요" description="입금이 완료되면 다음 단계에서 포인트를 바로 받을 수 있습니다." />
                            <div className="mt-[18px] rounded-[8px] border border-[#C3D4F4] bg-[#F8FAFF] p-[16px]">
                                <div className="flex items-center gap-[8px]"><Landmark size={17} color="#3D73DD" /><span className="text-[12px] font-bold text-[#64748B]">라포 기업계좌</span></div>
                                {/* 계좌 정보는 서버 정책 값만 표시한다 — 하드코딩하면 계좌 변경 시 오입금이 발생한다 */}
                                {account ? (
                                    <>
                                        <p className="mt-[12px] text-[13px] text-[#475569]">{account.bank} · 예금주 {account.holder}</p>
                                        <div className="mt-[5px] flex items-center justify-between gap-[10px]">
                                            <b className="text-[20px] tracking-[0px] text-[#111827]">{account.accountNumber}</b>
                                            <button type="button" onClick={() => { void navigator.clipboard?.writeText(account.accountNumber); setCopied(true); }} className="inline-flex h-[32px] items-center gap-[6px] rounded-[8px] border border-[#A6BFEF] bg-white px-[9px] text-[12px] font-bold text-[#3D73DD]"><Copy size={14} /> {copied ? '복사됨' : '복사'}</button>
                                        </div>
                                    </>
                                ) : (
                                    <p className="mt-[12px] text-[13px] text-[#DC2626]">입금 계좌 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</p>
                                )}
                            </div>
                            {selectedTier && <div className="mt-[12px] rounded-[8px] bg-[#F8FAFC] p-[14px]"><SummaryRow label="입금 금액" value={money(selectedTier.price)} strong /><p className="mt-[6px] text-[11px] text-[#64748B]">할인이 적용된 실제 입금 금액입니다. ({point(selectedTier.pointAmount)} 지급)</p></div>}
                            <label className="mt-[18px] block text-[12px] font-bold text-[#475569]">입금 예정자명 <span className="text-[#DC2626]">*</span><input value={depositorName} onChange={(event) => setDepositorName(event.target.value)} placeholder="통장에 표시될 이름 그대로 입력" className="mt-[7px] h-[42px] w-full rounded-[8px] border border-[#CBD5E1] px-[12px] text-[14px] outline-none focus:border-[#3D73DD]" /><span className="mt-[6px] block text-[11px] font-normal text-[#94A3B8]">실제 통장에 찍힐 이름과 같아야 입금을 확인할 수 있습니다.</span></label>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <SectionTitle title="입금 내용을 확인해 주세요" description="입금을 완료했다면 내용을 확인하고 포인트를 바로 받으세요." />
                            <div className="mt-[18px] rounded-[8px] border border-[#E2E8F0] p-[16px]"><SummaryRow label="입금 예정자명" value={depositorName} /><SummaryRow label="입금 금액" value={money(selectedTier?.price ?? 0)} /><SummaryRow label="할인" value={discountAmount > 0 ? `-${money(discountAmount)}` : '없음'} accent /><div className="mt-[10px] border-t border-[#E2E8F0] pt-[10px]"><SummaryRow label="즉시 지급 포인트" value={point(selectedTier?.pointAmount ?? 0)} strong /></div></div>
                            <div className="mt-[14px] rounded-[8px] border border-[#D8E2F0] bg-[#F8FAFC] p-[14px]">
                                <p className="text-[12px] font-bold text-[#334155]">포인트 구매·환불 안내</p>
                                <p className="mt-[6px] text-[11px] leading-[18px] text-[#64748B]">포인트는 센터의 기프티콘 발송 서비스 이용을 위한 선불 이용권으로, 구매 후 현금 환불은 원칙적으로 제공하지 않습니다.</p>
                                <p className="mt-[6px] text-[11px] leading-[18px] text-[#64748B]">다만 관계 법령상 청약철회·환불 사유, 회사 귀책, 중복 입금·오입금·시스템 오류 또는 회사가 별도로 승인한 경우에는 예외로 합니다.</p>
                                <div className="mt-[9px] flex items-center gap-[4px] text-[#3D73DD]">
                                    <p className="text-[11px] font-bold">예외 환불 시 원구매 단가 비례 환불</p>
                                    <span className="group relative inline-flex">
                                        <button type="button" aria-label="원구매 단가 비례 환불 안내" className="inline-flex rounded-full text-[#3D73DD] outline-none focus-visible:ring-2 focus-visible:ring-[#3D73DD] focus-visible:ring-offset-2">
                                            <Info size={14} />
                                        </button>
                                        <span role="tooltip" className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 z-10 hidden w-[280px] -translate-x-1/2 rounded-[8px] bg-[#1E293B] px-[11px] py-[9px] text-[10.5px] font-normal leading-[16px] text-white shadow-[0_8px_20px_rgba(15,23,42,0.2)] group-hover:block group-focus-within:block">잔여 포인트는 해당 충전 건의 실제 입금 금액을 기준으로 비례 환산합니다. 예: {point(selectedTier?.pointAmount ?? 0)}를 {money(selectedTier?.price ?? 0)}에 구매한 뒤 {point(refundExamplePoint)}가 남으면 {money(refundExampleAmount)}이 환불됩니다.</span>
                                    </span>
                                </div>
                            </div>
                            <button type="button" aria-pressed={consent} onClick={() => setConsent((prev) => !prev)} className={`mt-[14px] flex w-full items-start gap-[10px] rounded-[8px] border p-[13px] text-left ${consent ? 'border-[#A6BFEF] bg-[#F8FAFF]' : 'border-[#E2E8F0] bg-white'}`}><span className={`mt-[1px] flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[5px] ${consent ? 'bg-[#3D73DD]' : 'text-[#94A3B8]'}`}>{consent ? <Check size={13} color="white" /> : <Square size={20} strokeWidth={2.2} />}</span><span><b className="text-[12px] text-[#334155]">입금 확인 절차 및 포인트 이용·환불 정책을 확인했습니다.</b><p className="mt-[4px] text-[11px] leading-[17px] text-[#64748B]">입금이 확인되지 않으면 포인트 사용이 제한될 수 있으며, 회사 확인 후 별도 결제 또는 입금 절차를 안내드릴 수 있습니다. 허위 입금 선언이 반복되면 서비스 이용이 제한될 수 있습니다.</p></span></button>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="pt-[32px] text-center">
                            <div className="mx-auto flex h-[58px] w-[58px] items-center justify-center rounded-full bg-[#E5F8F3]"><CheckCircle2 size={30} color="#00A793" /></div><h3 className="mt-[16px] text-[21px] font-bold text-[#111827]">포인트가 충전되었습니다</h3><p className="mt-[6px] text-[13px] leading-[20px] text-[#64748B]">입금 확인 전에도 선물에 바로 사용할 수 있습니다.<br />입금 확인은 영업일 기준 1일 내 처리돼요. 홈의 충전 내역에서 상태를 확인할 수 있어요.</p>
                            <div className="mt-[22px] rounded-[8px] border border-[#C3D4F4] bg-[#F8FAFF] p-[18px] text-left"><SummaryRow label="지급 포인트" value={`+${point(selectedTier?.pointAmount ?? 0)}`} accent strong /><SummaryRow label="현재 사용 가능 잔액" value={point(balance)} strong /><div className="mt-[10px] flex justify-end"><Badge tone="orange">입금 확인 중 {point(unconfirmedAmount)}</Badge></div></div>
                        </div>
                    )}
                </div>

                <div className="border-t border-[#E2E8F0] bg-white px-[24px] py-[16px]">
                    {step === 4 ? <button type="button" onClick={onClose} className="h-[44px] w-full rounded-[8px] bg-[#3D73DD] text-[13px] font-bold text-white">완료</button> : <div className="flex gap-[8px]">{step > 1 && <button type="button" onClick={() => setStep((prev) => Math.max(1, prev - 1) as 1 | 2 | 3)} className="h-[44px] flex-1 rounded-[8px] border border-[#CBD5E1] bg-white text-[13px] font-bold text-[#475569]">이전</button>}<button type="button" disabled={!selectedTier || isSubmitting || (step === 2 && (!depositorName.trim() || !account)) || (step === 3 && !consent)} onClick={goNext} className="h-[44px] flex-[2] rounded-[8px] bg-[#3D73DD] text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:bg-[#CBD5E1]">{step === 3 ? (isSubmitting ? '신청 중…' : '입금을 완료했습니다') : '다음'}</button></div>}
                </div>
            </aside>
        </div>
    );
};

const SummaryRow = ({ label, value, accent = false, strong = false }: { label: string; value: string; accent?: boolean; strong?: boolean }) => (
    <div className="flex items-center justify-between gap-[16px] py-[4px]"><span className={`${strong ? 'font-bold text-[#334155]' : 'text-[#64748B]'} text-[12px]`}>{label}</span><span className={`${accent ? 'text-[#00A793]' : 'text-[#111827]'} ${strong ? 'text-[16px]' : 'text-[13px]'} font-bold`}>{value}</span></div>
);

const InsightCard = ({ title, value, desc, tone, onClick }: { title: string; value: string; desc: React.ReactNode; tone: 'blue' | 'green' | 'orange' | 'pink'; onClick: () => void }) => {
    const toneClass = {
        blue: 'bg-[#ECF1FC] text-[#3D73DD]',
        green: 'bg-[#E5F8F3] text-[#008C7B]',
        orange: 'bg-[#FEF3C7] text-[#B45309]',
        pink: 'bg-[#FCE7F3] text-[#BE185D]',
    }[tone];

    return (
        <button type="button" onClick={onClick} className="min-h-[148px] rounded-[8px] border border-[#E2E8F0] bg-white p-[18px] text-left shadow-[0_6px_18px_rgba(15,23,42,0.04)] transition-colors hover:border-[#A6BFEF] hover:bg-[#FBFCFE]">
            <div className="mb-[12px] flex items-center justify-between gap-[8px]"><div className={`inline-flex h-[32px] min-w-0 items-center rounded-[8px] px-[10px] text-[13px] font-bold ${toneClass}`}>{title}</div><ChevronRight size={16} color="#94A3B8" /></div>
            <p className="text-[24px] font-bold text-[#111827]">{value}</p>
            <p className="mt-[4px] text-[13px] leading-[20px] text-[#64748B]">{desc}</p>
        </button>
    );
};

const SummaryPanel = ({
    title,
    rows,
    actionLabel,
    onAction,
    disabled = false,
    children,
}: {
    title: string;
    rows: [string, string][];
    actionLabel: string;
    onAction?: () => void;
    disabled?: boolean;
    children?: React.ReactNode;
}) => (
    <Card className="p-[22px]">
        <SectionTitle title={title} />
        <div className="mt-[16px] space-y-[10px]">
            {rows.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between border-b border-[#F1F5F9] pb-[10px]">
                    <span className="text-[13px] text-[#64748B]">{label}</span>
                    <b className="text-[14px] text-[#111827]">{value}</b>
                </div>
            ))}
        </div>
        <div className="mt-[14px]">{children}</div>
        <button
            type="button"
            onClick={onAction}
            disabled={disabled}
            className="mt-[14px] flex h-[42px] w-full items-center justify-center gap-[7px] rounded-[8px] bg-[#3D73DD] text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:bg-[#CBD5E1]"
        >
            {actionLabel}
            <ChevronRight size={16} />
        </button>
    </Card>
);

const ProgressBar = ({ value, tone = 'blue', compact = false }: { value: number; tone?: 'blue' | 'orange' | 'red' | 'gray'; compact?: boolean }) => {
    const color = {
        blue: '#3D73DD',
        orange: '#F59E0B',
        red: '#EF4444',
        gray: '#CBD5E1',
    }[tone];

    return (
        <div className={`${compact ? 'mt-[0px] h-[6px]' : 'mt-[9px] h-[8px]'} overflow-hidden rounded-full bg-[#E9EEF5]`}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, value))}%`, backgroundColor: color }} />
        </div>
    );
};

export default GifticonGiftPage;
